import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Users, Star, Building2, Mail, Phone, Calendar, Globe2, CheckCircle2, ArrowRight, Award, Clock, Shield, Target, Zap, ThumbsUp } from "lucide-react";
import { motion } from "framer-motion";

// Şehir verileri
const cityData = {
  istanbul: {
    name: "Istanbul",
    country: "Türkiye",
    title: "İstanbul Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "İstanbul'da profesyonel fuar standı tasarımı ve uygulaması. CNR Expo, Tüyap ve İFM'de 15+ yıllık deneyimle fuar stand yapımı. Hızlı teklif alın!",
    keywords: "İstanbul fuar standı, fuar stand tasarımı İstanbul, CNR Expo stand, Tüyap fuar standı, İstanbul exhibition stand, fuar stand yapımı İstanbul",
    description: "İstanbul, Avrupa ve Asya kıtalarını birleştiren eşsiz konumuyla dünya ticaretinin kalbi durumundadır. CNR Expo, Tüyap Beylikdüzü ve İstanbul Fuar Merkezi (İFM) gibi dünya standartlarındaki tesislerde yılda 200'den fazla uluslararası fuar düzenlenmektedir. ExpoStand olarak, İstanbul'da fuar standı yaptırmak isteyen katılımcı firmaları en deneyimli ve güvenilir contractor'larla buluşturuyoruz.",
    detailedDescription: "Platformumuz üzerinden İstanbul'daki doğrulanmış fuar standı contractor'larından hızlıca teklif alabilir, projelerinizi karşılaştırabilir ve size en uygun çözümü bulabilirsiniz. Tüm contractor'larımız deneyim ve referanslarına göre filtrelenmiştir.",
    images: [
      "https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?q=80&w=1600&auto=format&fit=crop", 
      "https://images.unsplash.com/photo-1524420533731-4c6c2338cefc?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1561457213-a526e5beb0c8?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: {
      suppliers: 150,
      contractors: 85,
      annualEvents: 200,
      visitors: "15M+",
      experience: "15+",
      projects: "2500+"
    },
    venues: [
      {
        name: "CNR Expo",
        area: "65,000 m²",
        halls: "13 Salon", 
        location: "Yeşilköy",
        description: "İstanbul'un en büyük fuar merkezi"
      },
      {
        name: "Tüyap Beylikdüzü",
        area: "100,000 m²", 
        halls: "12 Salon",
        location: "Beylikdüzü",
        description: "Avrupa yakası premium fuar merkezi"
      },
      {
        name: "İstanbul Fuar Merkezi (İFM)",
        area: "45,000 m²",
        halls: "8 Salon",
        location: "Bakırköy", 
        description: "Şehir merkezinde ulaşımı kolay fuar alanı"
      }
    ],
    majorEvents: [
      { name: "Istanbul Jewelry Show", date: "Mart", participants: "40,000+" },
      { name: "Turkeybuild Istanbul", date: "Mayıs", participants: "85,000+" },
      { name: "CNR Food Istanbul", date: "Ekim", participants: "60,000+" },
      { name: "Istanbul Furniture Fair", date: "Ocak", participants: "120,000+" },
      { name: "Energy Istanbul", date: "Kasım", participants: "55,000+" },
      { name: "Istanbul Auto Show", date: "Nisan", participants: "200,000+" },
      { name: "Wedding Expo Istanbul", date: "Şubat", participants: "150,000+" },
      { name: "Travel Türkiye", date: "Şubat", participants: "80,000+" }
    ],
    services: [
      { title: "Stand Tasarım Uzmanları",    description: "Yaratıcı tasarım yapan contractor'lardan teklif alın",            icon: "🎨" },
      { title: "Stand Kurulum Firmaları",    description: "Deneyimli kurulum ekipleri ile çalışan contractor'lar",            icon: "🔨" },
      { title: "AV Teknoloji Sağlayıcıları", description: "LED, ses sistemi, interaktif çözümler sunan firmalar",             icon: "📺" },
      { title: "Mobilya Tedarikçileri",      description: "Kaliteli fuar mobilyası kiralayan contractor'lar",                 icon: "🪑" },
      { title: "Grafik & Baskı",             description: "Büyük format baskı, branding görselleri ve tabelalar",            icon: "🖨️" },
      { title: "Lojistik & Nakliye",         description: "Stand malzemelerinin güvenli taşınması ve depolama",              icon: "🚚" },
      { title: "Özel İnşaat Çözümleri",      description: "Modüler ve özel stand inşaatı, anahtar teslim projeler",         icon: "🏗️" },
      { title: "Aydınlatma Tasarımı",        description: "Profesyonel fuar aydınlatma ve atmosfer yönetimi",               icon: "💡" },
    ],
    whyChooseUs: [
      {
        title: "Doğrulanmış Contractor'lar",
        description: "Tüm üyelerimiz referans ve portföy kontrolünden geçer",
        icon: Award
      },
      {
        title: "Hızlı Eşleştirme",
        description: "24 saat içinde size uygun contractor'larla buluşun",
        icon: Clock
      },
      {
        title: "Güvenli İşlem", 
        description: "Platformumuz üzerinden güvenli ödeme ve sözleşme imkanı",
        icon: Shield
      },
      {
        title: "Çoklu Teklif",
        description: "Birden fazla contractor'dan teklif alıp karşılaştırın",
        icon: Target
      }
    ],
    testimonials: [
      {
        name: "Ahmet Kaya",
        company: "TechnoVision Türkiye",
        text: "ExpoStand platformu sayesinde CNR Expo'da standımızı yaptıracak mükemmel bir contractor bulduk. 3 farklı firmadan teklif aldık, en uygununu seçtik. Sonuç harika oldu!",
        rating: 5,
        event: "Istanbul Tech Summit"
      },
      {
        name: "Elif Öztürk", 
        company: "Luxury Home Design",
        text: "Tüyap'ta mobilya fuarına katıldık. ExpoStand'dan bulduğumuz contractor ile çalışmak harika bir deneyimdi. Platform sayesinde doğru firmayı bulmak çok kolay oldu.",
        rating: 5,
        event: "Istanbul Furniture Fair"
      },
      {
        name: "Mehmet Demir",
        company: "GreenTech Solutions", 
        text: "Energy Istanbul fuarı için ExpoStand platformundan çevre dostu çözümler sunan bir contractor bulduk. Hem kaliteli hem de sürdürülebilir malzemeler kullandılar.",
        rating: 5,
        event: "Energy Istanbul"
      }
    ],
    faqs: [
      {
        question: "İstanbul'da fuar standı maliyeti nasıl hesaplanır?",
        answer: "Platformumuz üzerinden farklı contractor'lardan aldığınız teklifleri karşılaştırabilirsiniz. Genellikle temel standlar m² başına 150-300 TL, özel tasarım standlar 300-800 TL arasında değişmektedir. Birden fazla teklif almanızı öneriyoruz."
      },
      {
        question: "Stand kurulumu ne kadar sürer?",
        answer: "Seçeceğiniz contractor'a bağlı olarak değişir. Genellikle standart standlar 1-2 gün, özel tasarım standlar 2-4 gün sürmektedir. Teklif alırken kurulum süresini de contractor'lara sorabilirsiniz."
      },
      {
        question: "Hangi fuar merkezlerinde contractor bulabiliyorum?", 
        answer: "CNR Expo, Tüyap Beylikdüzü, İstanbul Fuar Merkezi (İFM) ve İstanbul'daki tüm fuar merkezlerinde deneyimi olan contractor'larımız mevcuttur. Her merkeze özel tecrübeli firmalarla çalışıyoruz."
      },
      {
        question: "Son dakika projeleri için contractor bulabilir miyim?",
        answer: "Evet, acil projeler için hızlı çalışabilen contractor'larımız mevcuttur. Platformda 'Acil Proje' filtresi ile bu konuda deneyimli firmaları bulabilir, 48 saat içinde çözüm üretebilecek contractor'lardan teklif alabilirsiniz."
      }
    ]
  },
  dubai: {
    name: "Dubai", 
    country: "UAE",
    title: "Dubai Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Dubai'de profesyonel fuar standı tasarımı ve uygulaması. DWTC ve DICC'de 12+ yıllık deneyimle fuar stand yapımı. Hızlı teklif alın!",
    keywords: "Dubai fuar standı, Dubai exhibition stand, DWTC stand, Dubai World Trade Centre, Dubai fuar stand yapımı, UAE exhibition contractors",
    description: "Dubai, Orta Doğu ve Afrika'nın en büyük ticaret merkezi olarak dünyaca tanınır. Dubai World Trade Centre (DWTC) ve Dubai International Convention Centre'da yılda 150'den fazla prestijli uluslararası etkinlik düzenlenmekte, 8 milyondan fazla ziyaretçi ağırlanmaktadır. Platformumuz üzerinden Dubai'daki en deneyimli contractor'lardan hızlıca teklif alabilirsiniz.",
    detailedDescription: "Dubai fuar standı sektöründe güvenilir contractor'larımızla tanışın. Projelerinizi karşılaştırabilir ve size en uygun çözümü bulabilirsiniz.",
    images: [
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxEdWJhaSUyMHNreWxpbmV8ZW58MHx8fHwxNzYwMTcwMjMyfDA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1571850176963-86ebb086ba68?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1544672236-f6440b11392f?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1580674684081-7617fbf3d745?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: {
      suppliers: 120,
      contractors: 65,
      annualEvents: 150,
      visitors: "8M+",
      experience: "12+", 
      projects: "1800+"
    },
    venues: [
      {
        name: "Dubai World Trade Centre (DWTC)",
        area: "120,000 m²",
        halls: "8 Salon",
        location: "Trade Centre",
        description: "Orta Doğu'nun en prestijli fuar merkezi"
      },
      {
        name: "Dubai International Convention Centre",
        area: "65,000 m²",
        halls: "6 Salon", 
        location: "Festival City",
        description: "Modern teknoloji ile donatılmış fuar alanı"
      },
      {
        name: "Expo City Dubai",
        area: "200,000 m²",
        halls: "15+ Pavilion",
        location: "Expo City",
        description: "2020 Expo'nun mirası olan dev fuar kompleksi"
      }
    ],
    majorEvents: [
      { name: "Arab Health", date: "Ocak", participants: "85,000+" },
      { name: "Gulfood", date: "Şubat", participants: "98,000+" },
      { name: "GITEX Global", date: "Ekim", participants: "170,000+" },
      { name: "Dubai Airshow", date: "Kasım", participants: "80,000+" },
      { name: "ADIPEC", date: "Ekim", participants: "155,000+" },
      { name: "Big 5 Global", date: "Kasım", participants: "75,000+" },
      { name: "Dubai Watch Week", date: "Kasım", participants: "25,000+" },
      { name: "CABSAT", date: "Mart", participants: "14,000+" }
    ],
    services: [
      { title: "Lüks Stand Tasarımı",       description: "Dubai standardında premium stand çözümleri",                     icon: "✨" },
      { title: "Hızlı Kurulum",             description: "Profesyonel ekiplerle express kurulum hizmeti",                  icon: "⚡" },
      { title: "Premium AV Teknoloji",      description: "Son teknoloji LED, hologram ve interaktif çözümler",             icon: "🎬" },
      { title: "VIP Hizmetler",             description: "Dubai'ye özel lüks mobilya ve catering hizmetleri",              icon: "👑" },
      { title: "Özel İnşaat & Dekorasyon",  description: "World Trade Centre Dubai standartlarında özel yapılar",          icon: "🏛️" },
      { title: "Grafik & Dijital Baskı",    description: "Büyük format baskı, LED ekran ve dijital tabela",                icon: "🖨️" },
      { title: "Stand Lojistiği",           description: "Dubai gümrük ve nakliye süreçlerinde tam destek",                icon: "🚛" },
      { title: "Aydınlatma Tasarımı",       description: "Dubai'nin göz alıcı ışık estetiğini standınıza taşıyın",        icon: "💡" },
    ],
    whyChooseUs: [
      {
        title: "UAE Lisanslı Firmalar",
        description: "Dubai Municipality onaylı contractor'lar",
        icon: Award
      },
      {
        title: "Express Hizmet",
        description: "12 saat içinde contractor eşleştirme",
        icon: Clock
      },
      {
        title: "Lüks Standart",
        description: "Dubai kalite standartlarında premium hizmet",
        icon: Shield
      },
      {
        title: "Çok Para Birimleri",
        description: "AED, USD, EUR ile esnek ödeme seçenekleri",
        icon: Target
      }
    ],
    testimonials: [
      {
        name: "Ahmed Al-Rashid",
        company: "Emirates Tech Solutions",
        text: "GITEX'te standımız için ExpoStand platformunu kullandık. Dubai'deki en iyi contractor'la çalıştık ve muhteşem bir stand yaptırdık. Kesinlikle tavsiye ederim.",
        rating: 5,
        event: "GITEX Global"
      },
      {
        name: "Sarah Johnson",
        company: "MedTech International", 
        text: "Arab Health fuarında ExpoStand sayesinde mükemmel bir contractor bulduk. DWTC'deki standımız çok profesyoneldi ve hedeflenen ziyaretçi sayısını aştık.",
        rating: 5,
        event: "Arab Health"
      },
      {
        name: "Marco Rossi",
        company: "Italian Furniture Co",
        text: "Dubai'de ilk kez fuar organize ediyorduk. ExpoStand platformu sayesinde güvenilir bir contractor bulduk ve süreç çok sorunsuz geçti.",
        rating: 5,
        event: "Big 5 Global"
      }
    ],
    faqs: [
      {
        question: "Dubai'de fuar standı maliyeti nasıl hesaplanır?",
        answer: "Dubai'de maliyetler genellikle AED veya USD üzerinden belirlenir. Standart standlar m² başına 200-400 AED, premium standlar 400-1000 AED arasında değişir. Platformumuzdan çoklu teklif alarak en uygun fiyatı bulabilirsiniz."
      },
      {
        question: "Dubai'de fuar standı için izin gerekli mi?",
        answer: "Evet, Dubai Municipality'den izin gereklidir. Contractor'larımız tüm izin işlemlerini sizin adınıza halleder ve Dubai standartlarına uygun yapım sağlar."
      },
      {
        question: "DWTC ve DICC'de deneyimli contractor bulabilir miyim?",
        answer: "Tabii ki! Tüm contractor'larımız DWTC, DICC ve Expo City Dubai'de deneyimli firmalar. Her mekanın özel kurallarını ve lojistik gereksinimlerini bilirler."
      },
      {
        question: "Dubai'de son dakika projeler için contractor var mı?",
        answer: "Evet, Dubai'nin hızlı iş temposu için 24 saat çalışabilen contractor'larımız mevcut. Acil projeler için özel 'Express Service' seçeneğimizi kullanabilirsiniz."
      }
    ]
  },
  london: {
    name: "London",
    country: "United Kingdom", 
    title: "London Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "London'da profesyonel fuar standı tasarımı ve uygulaması. ExCeL London ve Olympia'da 18+ yıllık deneyimle fuar stand yapımı. Hızlı teklif alın!",
    keywords: "London exhibition stand, ExCeL London stand, Olympia exhibition, London trade show contractors, UK exhibition stand builders",
    description: "London, Avrupa'nın finans ve teknoloji başkenti olarak dünya çapında prestijli fuarlara ev sahipliği yapmaktadır. ExCeL London, Olympia ve Earl's Court'ta yılda 300'den fazla uluslararası etkinlik düzenlenmekte, 12 milyondan fazla ziyaretçi ağırlanmaktadır. İngiltere'nin en deneyimli contractor'larından hızlıca teklif alın.",
    detailedDescription: "London fuar standı sektöründe köklü geçmişi olan contractor'larımızla çalışın. British standartlarında kaliteli hizmet garantisi.",
    images: [
      "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1486299267070-83823f5448dd?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1529655683826-aba9b3e77383?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1484742150627-d0c43e9d269d?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1520637836862-4d197d17c6a4?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1526129318478-62ed807ebdf9?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: {
      suppliers: 200,
      contractors: 95, 
      annualEvents: 300,
      visitors: "12M+",
      experience: "18+",
      projects: "3200+"
    },
    venues: [
      {
        name: "ExCeL London",
        area: "100,000 m²",
        halls: "12 Hall",
        location: "Royal Docks",
        description: "Avrupa'nın en büyük fuar merkezi"
      },
      {
        name: "Olympia London", 
        area: "45,000 m²",
        halls: "4 Hall",
        location: "Kensington",
        description: "Londra'nın tarihi fuar merkezi" 
      },
      {
        name: "Business Design Centre",
        area: "25,000 m²",
        halls: "6 Suite",
        location: "Islington", 
        description: "Boutique etkinlikler için premium mekan"
      }
    ],
    majorEvents: [
      { name: "London Tech Week", date: "Haziran", participants: "65,000+" },
      { name: "World Travel Market", date: "Kasım", participants: "50,000+" },
      { name: "BETT Show", date: "Mart", participants: "35,000+" },
      { name: "London Fashion Week", date: "Şubat/Eylül", participants: "5,000+" },
      { name: "Security & Counter Terror Expo", date: "Mart", participants: "10,000+" },
      { name: "London Book Fair", date: "Nisan", participants: "25,000+" },
      { name: "Professional Beauty London", date: "Şubat", participants: "40,000+" },
      { name: "Vitality Health & Wellbeing Show", date: "Mayıs", participants: "30,000+" }
    ],
    services: [
      { title: "British Standard Tasarım",  description: "İngiltere kalite standartlarında stand tasarımı",                icon: "🇬🇧" },
      { title: "Çevre Dostu Çözümler",      description: "Sürdürülebilir malzemelerle yeşil stand yapımı",                 icon: "🌿" },
      { title: "Premium Teknoloji",         description: "London merkezli tech firmaların tercih ettiği AV çözümler",      icon: "💻" },
      { title: "Lüks Mobilya",              description: "British tarzı şık ve kaliteli fuar mobilyaları",                 icon: "🪑" },
      { title: "Grafik & Baskı",            description: "Büyük format dijital baskı ve branding çözümleri",               icon: "🖨️" },
      { title: "Stand Lojistiği",           description: "ExCeL/Olympia giriş lojistiği, depolama ve nakliye",             icon: "🚚" },
      { title: "Aydınlatma Sistemleri",     description: "Atmosfer yaratan profesyonel fuar aydınlatma çözümleri",         icon: "💡" },
      { title: "Özel Stand İnşaatı",        description: "Çift katlı ve bespoke stand inşaatı, anahtar teslim",           icon: "🏗️" },
    ],
    whyChooseUs: [
      {
        title: "UK Sertifikalı Firmalar",
        description: "Health & Safety Executive onaylı contractor'lar",
        icon: Award
      },
      {
        title: "Zamanında Teslimat",
        description: "British punctuality ile 18 saat içinde cevap",
        icon: Clock
      },
      {
        title: "Kalite Garantisi",
        description: "BS EN standartlarında çalışma garantisi",
        icon: Shield
      },
      {
        title: "GBP Fiyatlama",
        description: "Pound sterling ile şeffaf ve sabit fiyat",
        icon: Target
      }
    ],
    testimonials: [
      {
        name: "James Richardson",
        company: "FinTech London Ltd",
        text: "London Tech Week'te standımız için ExpoStand kullandık. ExCeL London'da harika bir contractor bulduk, tüm süreç çok profesyoneldi.",
        rating: 5,
        event: "London Tech Week"
      },
      {
        name: "Emily Watson",
        company: "British Fashion Brands",
        text: "Olympia'da Fashion Week etkinliğimiz için ExpoStand'dan bulduğumuz contractor ile çalıştık. Sonuç mükemmeldi, çok memnun kaldık.",
        rating: 5,
        event: "London Fashion Week"
      },
      {
        name: "David Thompson", 
        company: "Travel Solutions UK",
        text: "WTM fuarı için platform sayesinde güvenilir bir contractor bulduk. İngiliz kalitesi ve zamanında teslimat mükemmeldi.",
        rating: 5,
        event: "World Travel Market"
      }
    ],
    faqs: [
      {
        question: "London'da fuar standı maliyeti nasıl hesaplanır?",
        answer: "London'da fiyatlar GBP üzerinden belirlenir. Standart standlar m² başına £150-300, premium tasarım standlar £300-800 arasında değişir. VAT dahil tüm maliyetler şeffaf şekilde sunulur."
      },
      {
        question: "İngiltere'de fuar standı için özel gereksinimler var mı?",
        answer: "Evet, Health & Safety Executive (HSE) standartları ve fire safety regulations'a uyum zorunludur. Tüm contractor'larımız bu gereksinimleri karşılar."
      },
      {
        question: "ExCeL London ve Olympia'da deneyimli contractor bulabilir miyim?",
        answer: "Kesinlikle! Platform üzerindeki tüm contractor'lar bu mekanların özel gereksinimlerini iyi bilir ve union labor kurallarına hakimdir."
      },
      {
        question: "Brexit sonrası EU firmalar için özel süreç var mı?",
        answer: "Evet, EU firmalar için customs ve import documentation konularında destek sağlayan contractor'larımız mevcut. Tüm yasal süreçlerde rehberlik yaparlar."
      }
    ]
  },
  paris: {
    name: "Paris",
    country: "France",
    title: "Paris Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand", 
    metaDescription: "Paris'te profesyonel fuar standı tasarımı ve uygulaması. Porte de Versailles ve Le Bourget'te 20+ yıllık deneyimle fuar stand yapımı. Hızlı teklif alın!",
    keywords: "Paris foire stand, Porte de Versailles stand, Le Bourget exhibition, Paris salon professionnel, French exhibition contractors",
    description: "Paris, dünya fuar sektörünün başkenti olarak moda, lüks ve teknolojide öncülük etmektedir. Porte de Versailles, Le Bourget ve Grand Palais gibi ikonik mekanlarda yılda 380'den fazla prestijli etkinlik düzenlenmekte, 35 milyondan fazla ziyaretçi ağırlanmaktadır. Fransız şıklığında contractor'lardan hızlıca teklif alın.",
    detailedDescription: "Paris fuar standı sektöründe savoir-faire geleneği olan contractor'larımızla tanışın. French elegance ve haute couture kalitesinde hizmet.",
    images: [
      "https://images.unsplash.com/photo-1657057611558-a7ecf6323aff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxQYXJpcyUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3NjAxOTQ2MTl8MA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1431274172761-fca41d930114?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1549144511-f099e773c147?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1550340499-a6c60fc8287c?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: {
      suppliers: 180,
      contractors: 88,
      annualEvents: 380,
      visitors: "35M+",
      experience: "20+",
      projects: "4500+"
    },
    venues: [
      {
        name: "Paris Expo Porte de Versailles",
        area: "230,000 m²",
        halls: "8 Pavilyon",
        location: "15e arrondissement", 
        description: "Avrupa'nın en büyük fuar merkezi"
      },
      {
        name: "Le Bourget",
        area: "180,000 m²", 
        halls: "6 Hall",
        location: "Seine-Saint-Denis",
        description: "Havacılık ve teknoloji fuarlarının merkezi"
      },
      {
        name: "Grand Palais Éphémère",
        area: "35,000 m²",
        halls: "3 Espace",
        location: "Champ de Mars",
        description: "Lüks etkinlikler için prestijli mekan"
      }
    ],
    majorEvents: [
      { name: "Maison & Objet", date: "Ocak/Eylül", participants: "85,000+" },
      { name: "Paris Air Show (Le Bourget)", date: "Haziran", participants: "320,000+" },
      { name: "SIAL Paris", date: "Ekim", participants: "160,000+" },
      { name: "Premiere Vision Paris", date: "Şubat/Eylül", participants: "60,000+" },
      { name: "Batimat", date: "Kasım", participants: "400,000+" },
      { name: "Salon du Chocolat", date: "Ekim", participants: "120,000+" },
      { name: "Paris Fashion Week", date: "Mart/Ekim", participants: "3,000+" },
      { name: "Viva Technology", date: "Haziran", participants: "150,000+" }
    ],
    services: [
      { title: "French Elegance Tasarım",   description: "Fransız şıklığı ve haute couture kalitesinde standlar",          icon: "🇫🇷" },
      { title: "Lüks Malzeme",              description: "Made in France kalitesinde premium malzemeler",                  icon: "💎" },
      { title: "Sanat Eseri Standlar",      description: "Paris sanat geleneği ile tasarlanmış özgün çözümler",            icon: "🎨" },
      { title: "Gourmet Catering",          description: "French cuisine ile özel catering hizmetleri",                    icon: "🍷" },
      { title: "AV & Dijital Entegrasyon",  description: "Palais des Expositions seviyesinde AV ve dijital sistemler",     icon: "📺" },
      { title: "Aydınlatma & Atmosfer",     description: "Paris ışıklarından ilham alan dekoratif aydınlatma",             icon: "💡" },
      { title: "Stand Lojistiği",           description: "Paris fuarları için gümrük ve nakliye koordinasyonu",            icon: "🚚" },
      { title: "Grafik Prodüksiyon",        description: "Fransız estetik anlayışıyla büyük format baskı ve branding",     icon: "🖨️" },
    ],
    whyChooseUs: [
      {
        title: "French Certification",
        description: "QUALIBAT sertifikalı contractor'lar",
        icon: Award
      },
      {
        title: "Savoir-Faire Geleneği",
        description: "20+ yıllık French craftsmanship deneyimi",
        icon: Clock
      },
      {
        title: "Lüks Kalite",
        description: "Haute couture standartlarında işçilik garantisi",
        icon: Shield
      },
      {
        title: "EUR Şeffaflık",
        description: "Euro ile net fiyatlama, gizli maliyet yok",
        icon: Target
      }
    ],
    testimonials: [
      {
        name: "Pierre Dubois",
        company: "Maison Luxe Paris",
        text: "Maison & Objet fuarında ExpoStand sayesinde mükemmel bir contractor bulduk. Parisli contractor'ın savoir-faire'i ve şıklığı standımıza yansıdı.",
        rating: 5,
        event: "Maison & Objet"
      },
      {
        name: "Marie Laurent",
        company: "French Tech Solutions",
        text: "Viva Technology'de standımız için platform sayesinde harika bir ekip bulduk. French elegance ve modern teknoloji mükemmel uyumdu.",
        rating: 5,
        event: "Viva Technology"
      },
      {
        name: "Jean-Claude Martin",
        company: "Champagne & Co",
        text: "SIAL Paris'te gıda sektöründeki tecrübeleri ile mükemmel bir stand yaptırdık. French quality at its best!",
        rating: 5,
        event: "SIAL Paris"
      }
    ],
    faqs: [
      {
        question: "Paris'te fuar standı maliyeti nasıl hesaplanır?",
        answer: "Paris'te fiyatlar EUR üzerinden belirlenir ve oldukça prestijlidir. Standart standlar m² başına €200-500, haute couture tasarım standlar €500-1500 arasında değişir. TVA dahil tüm maliyetler belirtilir."
      },
      {
        question: "Fransız fuar mevzuatına uyum gerekli mi?",
        answer: "Evet, Code du travail ve sécurité incendie kurallarına uyum zorunludur. Tüm contractor'larımız French regulations konusunda uzman ve sertifikalıdır."
      },
      {
        question: "Porte de Versailles ve Le Bourget'te özel gereksinimler var mı?",
        answer: "Her mekanın kendine özel kuralları vardır. Contractor'larımız union syndicats ile çalışır ve CGT kurallarına tamamen uyum sağlar."
      },
      {
        question: "Paris'te moda ve lüks sektör deneyimi olan contractor bulabilir miyim?",
        answer: "Kesinlikle! Maison & Objet ve Paris Fashion Week'e özel contractor'larımız mevcut. Luxury brands ile çalışma deneyimi garantilidir."
      }
    ]
  },
  "las-vegas": {
    name: "Las Vegas",
    country: "United States",
    title: "Las Vegas Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Las Vegas'ta profesyonel fuar standı tasarımı ve uygulaması. LVCC ve Sands Expo'da 15+ yıllık deneyimle fuar stand yapımı. Hızlı teklif alın!",
    keywords: "Las Vegas exhibition stand, LVCC trade show, Sands Expo contractors, Vegas convention center, Nevada exhibition builders",
    description: "Las Vegas, Amerika'nın en büyük fuar ve konveksiyon şehri olarak Las Vegas Convention Center (LVCC) ve Sands Expo'da yılda 250'den fazla spektaküler etkinliğe ev sahipliği yapmaktadır. CES, NAB Show ve SEMA gibi dünyaca ünlü fuarlarda 22 milyondan fazla katılımcı ağırlanmaktadır. Vegas tarzı gösterişli contractor'lardan hızlıca teklif alın.",
    detailedDescription: "Las Vegas fuar standı sektöründe showbusiness kalitesinde hizmet veren contractor'larımızla çalışın. American innovation ve Vegas spektaküler tarzı.",
    images: [
      "https://images.unsplash.com/photo-1755875341396-cdbd4698912e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxMYXMlMjBWZWdhcyUyMHN0cmlwJTIwc2t5bGluZXxlbnwwfHx8fDE3NjAxOTQwOTR8MA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1514539079130-25950c84af65?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1582719282630-9321dcf4734e?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1477414348463-c0eb7f1359b6?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1571677208593-8b6b0b1b5b2d?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1605538883669-825200433431?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: {
      suppliers: 150,
      contractors: 75,
      annualEvents: 250,
      visitors: "22M+",
      experience: "15+", 
      projects: "2800+"
    },
    venues: [
      {
        name: "Las Vegas Convention Center (LVCC)",
        area: "320,000 m²",
        halls: "10 Hall",
        location: "Paradise Road",
        description: "Amerika'nın en büyük fuar merkezi"
      },
      {
        name: "Sands Expo",
        area: "100,000 m²",
        halls: "6 Hall", 
        location: "The Strip",
        description: "Luxury fuar ve etkinlik merkezi"
      },
      {
        name: "Mandalay Bay Convention Center",
        area: "150,000 m²",
        halls: "8 Hall",
        location: "South Strip",
        description: "Resort entegreli premium fuar merkezi"
      }
    ],
    majorEvents: [
      { name: "CES (Consumer Electronics Show)", date: "Ocak", participants: "180,000+" },
      { name: "NAB Show", date: "Nisan", participants: "91,000+" },
      { name: "SEMA Show", date: "Kasım", participants: "70,000+" },
      { name: "MAGIC Fashion", date: "Şubat/Ağustos", participants: "85,000+" },
      { name: "World of Concrete", date: "Ocak", participants: "60,000+" },
      { name: "SHOT Show", date: "Ocak", participants: "64,000+" },
      { name: "CONEXPO-CON/AGG", date: "Mart", participants: "130,000+" },
      { name: "Money 20/20", date: "Ekim", participants: "15,000+" }
    ],
    services: [
      { title: "Vegas Spektaküler Tasarım",  description: "Las Vegas showbusiness tarzında gösterişli standlar",            icon: "🎰" },
      { title: "High-Tech AV Sistemler",     description: "Hollywood seviyesi LED, projeksiyon ve ses teknolojisi",          icon: "🎬" },
      { title: "24/7 Kurulum",               description: "Vegas never sleeps! 7/24 hızlı kurulum hizmeti",                 icon: "🌙" },
      { title: "VIP Hospitality",            description: "Casino tarzı luxury lounge ve entertainment hizmetleri",          icon: "🍸" },
      { title: "Large Format Printing",      description: "CES seviyesinde devasa format baskı ve dijital ekranlar",         icon: "🖨️" },
      { title: "Lojistik & Depolama",        description: "Las Vegas Convention Center kargo ve gümrük işlemleri",           icon: "🚛" },
      { title: "Modüler Stand Sistemleri",   description: "CES'ten NAB'a tüm fuarlar için taşınabilir modüler çözümler",    icon: "🧩" },
      { title: "Özel İnşaat & Yapı",         description: "Showstopper standlar için özel inşaat ve yüksek yapılar",        icon: "🏗️" },
    ],
    whyChooseUs: [
      {
        title: "Nevada Licensed",
        description: "Nevada State Board sertifikalı contractor'lar", 
        icon: Award
      },
      {
        title: "Vegas Speed",
        description: "8 saat içinde contractor matching, Vegas hızında!",
        icon: Clock
      },
      {
        title: "Showbiz Quality",
        description: "Las Vegas entertainment kalitesinde garanti",
        icon: Shield
      },
      {
        title: "USD Pricing",
        description: "Dollar ile sabit fiyat, no hidden costs",
        icon: Target
      }
    ],
    testimonials: [
      {
        name: "Mike Anderson",
        company: "TechVegas Corp",
        text: "CES'te standımız için ExpoStand kullandık. Las Vegas contractor'ımız showbiz kalitesinde muhteşem bir stand yaptı. Visitors were amazed!",
        rating: 5,
        event: "CES"
      },
      {
        name: "Jessica Rodriguez",
        company: "Fashion Forward USA",
        text: "MAGIC Fashion Show'da ExpoStand platform sayesinde harika bir contractor bulduk. Vegas glamour ve fashion expertise mükemmel kombinasyondu.",
        rating: 5,
        event: "MAGIC Fashion"
      },
      {
        name: "Robert Johnson",
        company: "Construction Innovations",
        text: "World of Concrete'te contractor'ımız Vegas engineering ve American quality ile mükemmel bir iş çıkardı. Highly recommended!",
        rating: 5,
        event: "World of Concrete"
      }
    ],
    faqs: [
      {
        question: "Las Vegas'ta fuar standı maliyeti nasıl hesaplanır?",
        answer: "Vegas'ta fiyatlar USD üzerinden belirlenir ve genellikle premium seviyededir. Standart standlar m² başına $180-350, spectacular design standlar $350-1000 arasında değişir. Tax rates Nevada'ya göre hesaplanır."
      },
      {
        question: "Nevada eyalet kurallarına uyum gerekli mi?",
        answer: "Evet, Nevada Building Code ve Fire Marshal regulations'a uyum zorunludur. Tüm contractor'larımız Nevada State lisanslı ve union certified'dır."
      },
      {
        question: "CES ve NAB Show gibi büyük fuarlarda deneyimli contractor bulabilir miyim?",
        answer: "Kesinlikle! Platform'daki contractor'lar LVCC ve Sands Expo'nun tüm özel gereksinimlerini bilir, union labor ve logistics konularında uzmanıdır."
      },
      {
        question: "Las Vegas'ta 24/7 hizmet gerçekten mevcut mu?",
        answer: "Evet! 'City that never sleeps' mottosu ile 7/24 kurulum, destek ve emergency service sağlıyoruz. Vegas contractor'lar bu tempoya alışkın."
      }
    ]
  },
  beijing: {
    name: "Beijing", 
    country: "China",
    title: "Beijing Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Beijing'de profesyonel fuar standı tasarımı ve uygulaması. CNCC ve Beijing Exhibition Center'da 12+ yıllık deneyimle fuar stand yapımı. Hızlı teklif alın!",
    keywords: "Beijing exhibition stand, China National Convention Center, Beijing trade show, 北京展览摊位, Chinese exhibition contractors",
    description: "Beijing, Asya-Pasifik bölgesinin en büyük ticaret merkezi olarak China National Convention Center (CNCC) ve Beijing Exhibition Center'da yılda 180'den fazla uluslararası etkinlik düzenlemektedir. Auto Show, Hi-Tech Fair ve Canton Fair gibi prestijli fuarlarda 28 milyondan fazla ziyaretçi ağırlanmaktadır. Çin'in en güvenilir contractor'larından hızlıca teklif alın.",
    detailedDescription: "Beijing fuar standı sektöründe Chinese craftsmanship ve modern teknoloji birleştiren contractor'larımızla çalışın. Made in China kalitesi ve competitive pricing.",
    images: [
      "https://images.unsplash.com/photo-1708660575990-101db3b00294?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxCZWlqaW5nJTIwc2t5bGluZXxlbnwwfHx8fDE3NjAxOTQwNzB8MA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1517732306149-e8f829eb588a?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1590760897065-48d3a92ebf06?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1564847458857-c574602ebe75?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1574790398781-e8ba5ba83964?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1580674684081-7617fbf3d745?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: {
      suppliers: 130,
      contractors: 58,
      annualEvents: 180,
      visitors: "28M+",
      experience: "12+",
      projects: "2200+"
    },
    venues: [
      {
        name: "China National Convention Center (CNCC)",
        area: "270,000 m²", 
        halls: "13 Hall",
        location: "Olympic Green",
        description: "2008 Olimpiyatları mirası dev fuar merkezi"
      },
      {
        name: "Beijing Exhibition Center",
        area: "80,000 m²",
        halls: "8 Hall",
        location: "Xicheng District", 
        description: "Beijing'in tarihi fuar merkezi"
      },
      {
        name: "China International Exhibition Center",
        area: "150,000 m²",
        halls: "15+ Hall",
        location: "Shunyi District",
        description: "Modern teknoloji fuarlarının merkezi"
      }
    ],
    majorEvents: [
      { name: "Beijing Auto Show", date: "Nisan", participants: "820,000+" },
      { name: "China Hi-Tech Fair (CHTF)", date: "Kasım", participants: "600,000+" },
      { name: "Beijing Design Week", date: "Eylül", participants: "350,000+" },
      { name: "China Construction Expo", date: "Mayıs", participants: "280,000+" },
      { name: "Beijing Book Fair", date: "Ağustos", participants: "450,000+" },
      { name: "China Electronics Fair", date: "Ekim", participants: "120,000+" },
      { name: "Beijing International Tourism Expo", date: "Haziran", participants: "180,000+" },
      { name: "China Energy Summit", date: "Mart", participants: "95,000+" }
    ],
    services: [
      { title: "Chinese Modern Tasarım",     description: "Geleneksel Çin estetiği ve modern teknoloji birleşimi",          icon: "🏮" },
      { title: "Cost-Effective Solutions",   description: "Competitive pricing ile yüksek kalite standlar",                 icon: "💰" },
      { title: "Tech Integration",           description: "Made in China teknoloji ve innovation showcase",                  icon: "📱" },
      { title: "Bilingual Service",          description: "Çince-İngilizce ikili dil desteği",                              icon: "🗣️" },
      { title: "Büyük Format Baskı",         description: "CNCC için devasa format baskı ve dijital tabela",                icon: "🖨️" },
      { title: "Stand Lojistiği & Nakliye",  description: "Beijing fuarları için lojistik ve gümrük koordinasyonu",        icon: "🚚" },
      { title: "Aydınlatma Tasarımı",        description: "Modern Çin estetiğine uygun atmosfer aydınlatması",              icon: "💡" },
      { title: "Modüler Stand Sistemleri",   description: "Birden fazla fuarda kullanılabilir modüler çözümler",            icon: "🧩" },
    ],
    whyChooseUs: [
      {
        title: "China Certified",
        description: "Chinese government sertifikalı contractor'lar",
        icon: Award
      },
      {
        title: "Fast Response",
        description: "Beijing time zone'da 12 saat içinde yanıt",
        icon: Clock
      },
      {
        title: "Quality Control",
        description: "ISO 9001 Chinese quality standards garantisi",
        icon: Shield
      },
      {
        title: "RMB/USD Pricing",
        description: "Yuan veya Dollar ile flexible payment options",
        icon: Target
      }
    ],
    testimonials: [
      {
        name: "Li Wei",
        company: "Beijing Tech Innovations",
        text: "Hi-Tech Fair'de standımız için ExpoStand kullandık. Chinese contractor'ımız hem teknoloji hem de geleneksel tasarımı mükemmel birleştirdi.",
        rating: 5,
        event: "China Hi-Tech Fair"
      },
      {
        name: "Sarah Chen",
        company: "International Auto Parts",
        text: "Beijing Auto Show'da platform sayesinde harika bir contractor bulduk. Chinese efficiency ve international quality standartları mükemmeldi.",
        rating: 5,
        event: "Beijing Auto Show"
      },
      {
        name: "Zhang Ming",
        company: "Construction Materials China",
        text: "Construction Expo'da ExpoStand contractor'ı ile çalışmak harikaydı. Hem cost-effective hem de high quality service aldık.",
        rating: 5, 
        event: "China Construction Expo"
      }
    ],
    faqs: [
      {
        question: "Beijing'de fuar standı maliyeti nasıl hesaplanır?",
        answer: "Beijing'de fiyatlar RMB veya USD üzerinden belirlenir ve oldukça competitive'dir. Standart standlar m² başına ¥800-1500, high-tech standlar ¥1500-4000 arasında değişir. Tax ve VAT dahil net fiyatlar sunulur."
      },
      {
        question: "Çin fuar mevzuatına uyum gerekli mi?",
        answer: "Evet, China Exhibition Industry Association (CEIA) ve local fire safety regulations'a uyum zorunludur. Tüm contractor'larımız Chinese standards'a tamamen uyumludur."
      },
      {
        question: "CNCC ve diğer Beijing merkezlerinde deneyimli contractor bulabilir miyim?",
        answer: "Kesinlikle! Platform'daki tüm contractor'lar Beijing'in major venues'larında deneyimli, Chinese bureaucracy ve logistics konularında uzman."
      },
      {
        question: "Language barrier konusunda destek var mı?",
        answer: "Evet, tüm contractor'larımız bilingual (Çince-İngilizce) hizmet verir. Ayrıca platform üzerinden translation support da sağlıyoruz."
      }
    ]
  },
  berlin: {
    name: "Berlin",
    country: "Almanya",
    title: "Berlin Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Berlin'de profesyonel fuar standı tasarımı ve uygulaması. Messe Berlin ve ICC'de 15+ yıllık deneyimle fuar stand yapımı. Hızlı teklif alın!",
    keywords: "Berlin fuar standı, Berlin exhibition stand, Messe Berlin stand, IFA Berlin stand, ITB Berlin exhibition, Berlin fuar stand yapımı",
    description: "Berlin, Avrupa'nın en dinamik ticaret ve teknoloji merkezlerinden biri olarak Messe Berlin'de yılda 100'den fazla uluslararası fuar ağırlamaktadır. IFA (Consumer Electronics), ITB (Travel & Tourism), InnoTrans (Transport Tech) ve Grüne Woche (Agriculture) gibi dünya devleri burada düzenlenir. 10 milyondan fazla yıllık ziyaretçiyle Berlin, Avrupa fuar takviminin vazgeçilmezi.",
    detailedDescription: "ExpoStand platformu üzerinden Berlin'deki doğrulanmış fuar standı contractor'larından hızlıca teklif alın. Almanya'nın teknik standartlarına uygun, deneyimli ekiplerle çalışın.",
    images: [
      "https://images.unsplash.com/photo-1560969184-10fe8719e047?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1528360983277-13d401cdc186?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1566404791232-af9fe8ae8677?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1551866442-64e75e911c23?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: { suppliers: 180, contractors: 95, annualEvents: 100, visitors: "10M+", experience: "15+", projects: "3000+" },
    venues: [
      { name: "Messe Berlin", area: "160,000 m²", halls: "27 Hall", location: "Charlottenburg", description: "Avrupa'nın en büyük fuar komplekslerinden biri" },
      { name: "ICC Berlin", area: "80,000 m²", halls: "80 Konferans Salonu", location: "Charlottenburg", description: "Uluslararası kongre ve fuar merkezi" },
      { name: "Station Berlin", area: "15,000 m²", halls: "4 Hall", location: "Kreuzberg", description: "Yaratıcı endüstriler için alternatif fuar alanı" }
    ],
    majorEvents: [
      { name: "IFA (Consumer Electronics)", date: "Eylül", participants: "250,000+" },
      { name: "ITB Berlin (Travel)", date: "Mart", participants: "160,000+" },
      { name: "InnoTrans (Transport)", date: "Eylül (çift yıl)", participants: "155,000+" },
      { name: "Grüne Woche (Agriculture)", date: "Ocak", participants: "400,000+" },
      { name: "Berlin Fashion Week", date: "Ocak/Temmuz", participants: "80,000+" },
      { name: "re:publica (Digital)", date: "Mayıs", participants: "25,000+" },
      { name: "IAA Mobility (Otomobil)", date: "Eylül (çift yıl)", participants: "500,000+" },
      { name: "DMEXCO (Dijital Pazarlama)", date: "Eylül", participants: "40,000+" }
    ],
    services: [
      { title: "Alman Mühendislik Kalitesi",  description: "DIN standartlarına uygun güçlü stand yapıları",                icon: "🔩" },
      { title: "Sürdürülebilir Çözümler",     description: "Çevre dostu malzeme ve enerji tasarruflu aydınlatma",           icon: "♻️" },
      { title: "Dijital Entegrasyon",         description: "Interaktif ekranlar, AR/VR ve tech showcase çözümleri",          icon: "📱" },
      { title: "Lojistik & Depolama",         description: "Stand elemanlarının depolanması ve kurulum lojistiği",           icon: "🚚" },
      { title: "Grafik & Baskı Sistemleri",   description: "IFA/ITB kalitesinde büyük format baskı ve LED tabelalar",       icon: "🖨️" },
      { title: "Aydınlatma & AV Tasarımı",    description: "Alman mühendisliğiyle optimize edilmiş fuar aydınlatması",      icon: "💡" },
      { title: "Mobilya Kiralama",            description: "Kaliteli ve ergonomik fuar mobilyası kiralama hizmetleri",      icon: "🪑" },
      { title: "Özel Stand İnşaatı",          description: "Alman işçiliğiyle çift katlı ve özel stand yapıları",           icon: "🏗️" },
    ],
    whyChooseUs: [
      { title: "TÜV Sertifikalı", description: "Alman güvenlik standartlarına uyumlu contractor'lar", icon: Award },
      { title: "Hızlı Yanıt", description: "CET saat dilimiyle 8 saat içinde teklif", icon: Clock },
      { title: "AB Uyumlu", description: "Avrupa güvenlik ve çevre mevzuatına tam uyum", icon: Shield },
      { title: "Rekabetçi Fiyat", description: "Birden fazla Alman contractor'dan karşılaştırmalı teklif", icon: Target }
    ],
    testimonials: [
      { name: "Klaus Müller", company: "AutoTech GmbH", text: "IFA Berlin'de standımız için ExpoStand'ı kullandık. Alman kalitesinde bir contractor bulduk, teslim tam zamanındaydı ve bütçemizin altında kaldık.", rating: 5, event: "IFA Berlin" },
      { name: "Ayşe Tekin", company: "Turkish Exports Ltd", text: "ITB Berlin'de Türk ürünlerini tanıtmak için harika bir stand yaptırdık. Platform üzerinden karşılaştırma yapmak çok kolaylaştı.", rating: 5, event: "ITB Berlin" },
      { name: "Hans Weber", company: "GreenTech Solutions", text: "Grüne Woche için sürdürülebilir malzeme kullanan bir contractor bulduk. ExpoStand sayesinde tam aradığımız firmayı hızla tespit ettik.", rating: 5, event: "Grüne Woche" }
    ],
    faqs: [
      { question: "Berlin'de fuar standı maliyeti nedir?", answer: "Berlin'de standart sistem standlar m² başına 150-350 EUR, özel tasarım standlar 350-800 EUR arasında değişir. Messe Berlin'de ek venue hizmet ücretleri de hesaba katılmalıdır." },
      { question: "Messe Berlin'in teknik gereksinimleri nelerdir?", answer: "Elektrik bağlantısı, yangın yönetmelikleri ve yük taşıma kapasitesi için önceden izin gerekir. Contractor'larımız tüm venue bürokratik süreçleri size adına yönetir." },
      { question: "IFA için stand ne zaman sipariş edilmeli?", answer: "IFA gibi büyük fuarlar için en az 6 ay önceden contractor seçmenizi öneririz. Bazı premium alanlar 9-12 ay önceden dolar." },
      { question: "Almanca bilmeden süreci yönetebilir miyim?", answer: "Evet. Platform üzerindeki contractor'ların büyük çoğunluğu İngilizce hizmet verir ve tüm resmi süreçleri sizin adınıza takip eder." }
    ]
  },
  frankfurt: {
    name: "Frankfurt",
    country: "Almanya",
    title: "Frankfurt Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Frankfurt'ta profesyonel fuar standı tasarımı. Frankfurter Messe'de IAA, Automechanika ve Book Fair için deneyimli stand yapımı. Teklif alın!",
    keywords: "Frankfurt fuar standı, Frankfurter Messe, Automechanika stand, IAA Frankfurt, Book Fair stand, Frankfurt exhibition stand contractors",
    description: "Frankfurt, Messe Frankfurt'un 592.000 m² alanıyla dünyanın en büyük fuar komplekslerinden birine ev sahipliği yapar. Automechanika, IAA Truck, Frankfurt Book Fair ve Ambiente gibi sektör devleri burada buluşur. Yılda 50'den fazla uluslararası fuar ile Frankfurt, Alman ekonomisinin küresel vitriniydi ve bugün de öyledir.",
    detailedDescription: "Messe Frankfurt'un zorlu teknik standartlarına hakim, akredite contractor'larla çalışın. ExpoStand platformu üzerinden karşılaştırmalı teklif alın.",
    images: [
      "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1578662996442-48f60103fc96?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1505761671935-60b3a7427bad?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1566404791232-af9fe8ae8677?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: { suppliers: 200, contractors: 110, annualEvents: 50, visitors: "20M+", experience: "20+", projects: "4000+" },
    venues: [
      { name: "Messe Frankfurt", area: "592,127 m²", halls: "12 Hall (12 adet)", location: "Frankfurt Batı", description: "Dünyanın en büyük fuar alanlarından biri" },
      { name: "Congress Center Messe", area: "16,500 m²", halls: "45 Konferans", location: "Messe Kampüsü", description: "Kongre ve B2B etkinlikler için" },
      { name: "Festhalle Frankfurt", area: "13,000 m²", halls: "1 Ana Salon", location: "Messe Kampüsü", description: "Tarihi ve prestijli etkinlik salonu" }
    ],
    majorEvents: [
      { name: "Automechanika (Otomotiv)", date: "Eylül (çift yıl)", participants: "130,000+" },
      { name: "Frankfurt Book Fair (Kitap)", date: "Ekim", participants: "300,000+" },
      { name: "Ambiente (Ev & Yaşam)", date: "Şubat", participants: "140,000+" },
      { name: "Light + Building (Aydınlatma)", date: "Mart (çift yıl)", participants: "220,000+" },
      { name: "Achema (Kimya/Pharma)", date: "Haziran (3 yılda bir)", participants: "145,000+" },
      { name: "Christmasworld", date: "Ocak", participants: "50,000+" },
      { name: "ISH (Su & HVAC)", date: "Mart (çift yıl)", participants: "190,000+" },
      { name: "Tendence (Ev Dekorasyonu)", date: "Ağustos", participants: "55,000+" }
    ],
    services: [
      { title: "Otomotiv Stand Uzmanlığı",   description: "Automechanika ve IAA için özel araç teşhir çözümleri",          icon: "🚗" },
      { title: "B2B Showroom Tasarımı",      description: "Ambiente ve Christmasworld için ürün teşhir standları",          icon: "🛍️" },
      { title: "Çift Katlı Stand",           description: "Büyük fuarlar için üst katlı ofis ve teşhir alanları",           icon: "🏢" },
      { title: "Işık & Ses Sistemleri",      description: "Light+Building fuarı için profesyonel AV entegrasyonu",           icon: "💡" },
      { title: "Stand Lojistiği & Nakliye",  description: "Messe Frankfurt kargo ve depolama süreçlerinde tam destek",      icon: "🚚" },
      { title: "Grafik & Baskı Çözümleri",  description: "Frankfurter Messe için büyük format baskı ve dijital görseller",  icon: "🖨️" },
      { title: "Mobilya & Dekorasyon",       description: "Ambiente estetiğiyle premium stand mobilyası kiralama",           icon: "🪑" },
      { title: "Anahtar Teslim İnşaat",      description: "Konsept tasarımdan kuruluma, Messe Frankfurt standartlarında",   icon: "🏗️" },
    ],
    whyChooseUs: [
      { title: "Messe Frankfurt Akreditasyonu", description: "Messe Frankfurt onaylı contractor'larla güvenli çalışın", icon: Award },
      { title: "Çok Yıllık Deneyim", description: "20+ yıl Messe Frankfurt projelerinde uzmanlaşmış ekipler", icon: Clock },
      { title: "Teknik Güvence", description: "Alman kalite standartlarında anahtar teslim projeler", icon: Shield },
      { title: "Esnek Bütçe", description: "Küçük booth'tan büyük paviyon'a her bütçeye çözüm", icon: Target }
    ],
    testimonials: [
      { name: "Marco Rossi", company: "Italian Auto Parts", text: "Automechanika Frankfurt'ta 3 yıldır ExpoStand ile çalışıyoruz. Her seferinde aynı kalite, tam zamanında teslimat.", rating: 5, event: "Automechanika Frankfurt" },
      { name: "Zeynep Kaya", company: "Turkish Textile Ltd", text: "Heimtextil fuarı için Frankfurt'ta stand yaptırdık. ExpoStand sayesinde Alman kalitesinde bir contractor bulduk.", rating: 5, event: "Heimtextil Frankfurt" },
      { name: "Patrick Schulz", company: "TechChem Solutions", text: "Achema için karmaşık lab demosunu gösteren stand tasarımını ExpoStand contractor'ı mükemmel çıkardı.", rating: 5, event: "Achema Frankfurt" }
    ],
    faqs: [
      { question: "Frankfurt Messe'de stand kurulum izni nasıl alınır?", answer: "Messe Frankfurt'un Exhibitor Services portalı üzerinden online başvuru yapılır. Contractor'larımız tüm teknik çizim ve onay süreçlerini sizin adınıza yürütür." },
      { question: "Automechanika için özel gereksinimler var mı?", answer: "Araç teşhiri için zemin yük kapasitesi ve yakıt/yağ güvenlik tedbirleri zorunludur. Contractor'larımız tüm Messe Frankfurt güvenlik protokollerine hakimdir." },
      { question: "Şehir merkezinden Messe Frankfurt'a ulaşım nasıl?", answer: "Frankfurt Hauptbahnhof'tan U4 metro hattıyla 10 dakikada ulaşılır. Ayrıca şehrin her noktasından taxis ve Uber erişimi de mevcuttur." },
      { question: "Frankfurt'ta stand depolama imkanı var mı?", answer: "Evet, contractor'larımızın büyük çoğunluğu Frankfurt yakınında depolama tesisi işletmektedir. Yıllık kiralama ile maliyetleri azaltabilirsiniz." }
    ]
  },
  milan: {
    name: "Milan",
    country: "İtalya",
    title: "Milano Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Milano'da profesyonel fuar standı tasarımı. Fiera Milano'da EuroShop, Host ve EICMA için İtalyan tasarım kalitesinde stand yapımı. Teklif alın!",
    keywords: "Milano fuar standı, Milan exhibition stand, Fiera Milano stand, EuroShop Milan, Host Milan exhibition, Milan exhibition contractors",
    description: "Milano, moda, tasarım ve teknolojiyi buluşturan eşsiz bir fuar şehridir. Fiera Milano — Avrupa'nın en büyük fuar kompleksi (345.000 m²) — EuroShop (Perakende), Salone del Mobile (Mobilya), EICMA (Motosiklet), Host Milano (Gastronomi) gibi sektör öncüsü fuarlara ev sahipliği yapar. İtalyan estetik mükemmeliyetle buluşan stand tasarımı için doğru platform.",
    detailedDescription: "ExpoStand üzerinden Milano'nun en yaratıcı ve deneyimli stand contractor'larına ulaşın. Made in Italy kalitesiyle fuarınızda fark yaratın.",
    images: [
      "https://images.unsplash.com/photo-1520175480921-4edfa2983e0f?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1469041797191-50ace28483c3?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1534430480872-3498386e7856?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515578154767-4b7f4a4c9c0e?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: { suppliers: 160, contractors: 90, annualEvents: 80, visitors: "12M+", experience: "18+", projects: "2800+" },
    venues: [
      { name: "Fiera Milano (Rho)", area: "345,000 m²", halls: "24 Hall", location: "Rho (Metro B)", description: "Avrupa'nın en büyük fuar alanı" },
      { name: "Fiera Milano City", area: "71,000 m²", halls: "9 Hall", location: "Milano Merkez", description: "Şehir merkezindeki tarihi fuar merkezi" },
      { name: "MiCo – Milano Congressi", area: "70,000 m²", halls: "40+ Salon", location: "Portello", description: "Uluslararası kongre ve fuarlar" }
    ],
    majorEvents: [
      { name: "Salone del Mobile (Mobilya)", date: "Nisan", participants: "435,000+" },
      { name: "EICMA (Motosiklet)", date: "Kasım", participants: "488,000+" },
      { name: "Host Milano (Gastronomi)", date: "Ekim (tek yıl)", participants: "165,000+" },
      { name: "MICAM (Ayakkabı/Moda)", date: "Eylül/Şubat", participants: "37,000+" },
      { name: "EuroShop (Perakende)", date: "Şubat (3 yılda bir)", participants: "94,000+" },
      { name: "Milano Unica (Tekstil)", date: "Şubat/Temmuz", participants: "12,000+" },
      { name: "Tuttofood (Gıda)", date: "Mayıs", participants: "80,000+" },
      { name: "BIT (Turizm)", date: "Şubat", participants: "50,000+" }
    ],
    services: [
      { title: "İtalyan Tasarım Estetiği",   description: "Made in Italy estetik anlayışıyla premium stand tasarımı",       icon: "🎨" },
      { title: "Lüks Malzeme & Finish",      description: "Mermer, ahşap, deri ve premium kumaş uygulamaları",              icon: "✨" },
      { title: "Moda & Showroom Standları",  description: "MICAM ve Milano Unica için haute couture standlar",               icon: "👗" },
      { title: "Gastronomi Teşhir",          description: "Host Milano için mutfak ekipmanı teşhir standları",               icon: "🍽️" },
      { title: "AV & Tech Entegrasyonu",     description: "Fiera Milano seviyesinde interaktif dijital çözümler",            icon: "📺" },
      { title: "Aydınlatma & Atmosfer",      description: "İtalyan ışık tasarımıyla büyüleyici stand atmosferi",             icon: "💡" },
      { title: "Stand Lojistiği",            description: "Fiera Rho ve Milano Congressi için taşıma koordinasyonu",        icon: "🚚" },
      { title: "Grafik & Prodüksiyon",       description: "Salone del Mobile estetiğiyle büyük format baskı ve branding",   icon: "🖨️" },
    ],
    whyChooseUs: [
      { title: "Made in Italy Kalite", description: "İtalyan zanaatkarlarla üretilmiş premium stand malzemeleri", icon: Award },
      { title: "Fiera Milano Onaylı", description: "Fiera Milano'nun tüm venue kurallarına uyumlu ekipler", icon: Clock },
      { title: "Yaratıcı Tasarım", description: "Salone del Mobile estetiğiyle öne çıkan tasarımlar", icon: Shield },
      { title: "Anahtar Teslim Proje", description: "Tasarımdan kuruluma, dekorasyona kadar tam hizmet", icon: Target }
    ],
    testimonials: [
      { name: "Luca Ferrari", company: "Italian Furniture Designs", text: "Salone del Mobile için ExpoStand'dan bulduğumuz contractor inanılmaz bir stand tasarladı. Made in Italy kalitesi gerçekten hissediliyordu.", rating: 5, event: "Salone del Mobile" },
      { name: "Selin Yıldız", company: "Turkish Textile Exports", text: "Milano Unica'da Türk kumaşlarını sergilemek için harika bir stand yaptırdık. İtalyan estetik ile Türk kalitesi harika buluştu.", rating: 5, event: "Milano Unica" },
      { name: "Giuseppe Marino", company: "Horeca Equipment Italy", text: "Host Milano için açık mutfak teşhiri yaptık. ExpoStand contractor'ı teknik ve estetik dengeyi mükemmel kurdu.", rating: 5, event: "Host Milano" }
    ],
    faqs: [
      { question: "Fiera Milano'ya ulaşım nasıl?", answer: "Milano Centrale'den Metro M1 (Rho Fiera Milano) ile 30 dakika. Shuttle servisler de mevcuttur. Contractor'larımız lojistik planlamanızda da yardımcı olabilir." },
      { question: "Salone del Mobile için stand ne zaman sipariş edilmeli?", answer: "Salone del Mobile çok yoğun geçtiğinden en az 8-10 ay önceden harekete geçin. Bazı premium contractor'lar çok daha erken dolar." },
      { question: "İtalya'da fatura ve KDV nasıl işliyor?", answer: "İtalya'da KDV (IVA) oranı %22'dir. AB dışı firmalar için çeşitli muafiyet mekanizmaları bulunmaktadır. Contractor'larımız muhasebe süreçlerinde rehberlik edebilir." },
      { question: "Stand malzemelerini Milano'da depolayabilir miyim?", answer: "Evet, Rho-Pero bölgesinde pek çok contractor depolama imkanı sunar. Uzun vadeli kiralama ile her sene nakliye maliyetinden tasarruf edersiniz." }
    ]
  },
  amsterdam: {
    name: "Amsterdam",
    country: "Hollanda",
    title: "Amsterdam Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Amsterdam'da profesyonel fuar standı tasarımı. RAI Amsterdam'da ISE, TFWA ve Horecava için deneyimli stand yapımı. Hızlı teklif alın!",
    keywords: "Amsterdam fuar standı, Amsterdam exhibition stand, RAI Amsterdam stand, ISE Amsterdam, Amsterdam Rai stand builder, Netherlands exhibition contractors",
    description: "Amsterdam, Avrupa'nın en stratejik fuar lokasyonlarından biri olarak RAI Amsterdam'da yılda 60'tan fazla uluslararası etkinlik düzenler. ISE (Integrated Systems Europe — dünyanın en büyük AV fuarı), TFWA (Travel Retail), Horecava (Gastronomi) ve IECC gibi etkinliklere ev sahipliği yapar. Schiphol Havalimanı'na 15 dakika mesafesiyle global katılımcılar için ideal konumdadır.",
    detailedDescription: "ExpoStand üzerinden Hollanda'nın yenilikçi ve sürdürülebilir stand contractor'larına ulaşın. Dutch design anlayışıyla minimal ve etkili standlar için teklif alın.",
    images: [
      "https://images.unsplash.com/photo-1534351590666-13e3e96b5017?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1580674684081-7617fbf3d745?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1471874708374-c9dc54b8b773?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: { suppliers: 140, contractors: 75, annualEvents: 60, visitors: "7M+", experience: "14+", projects: "1900+" },
    venues: [
      { name: "RAI Amsterdam", area: "100,000 m²", halls: "11 Hall", location: "Amsterdam Güney", description: "Hollanda'nın en büyük fuar ve kongre merkezi" },
      { name: "Amsterdam Arena Boulevard", area: "45,000 m²", halls: "Çoklu", location: "Amsterdam SE", description: "Johan Cruyff Arena çevresindeki etkinlik kompleksi" },
      { name: "Beurs van Berlage", area: "10,000 m²", halls: "8 Salon", location: "Amsterdam Centrum", description: "Tarihi borsa binası — premium etkinlikler" }
    ],
    majorEvents: [
      { name: "ISE (AV & Systems Integration)", date: "Şubat", participants: "82,000+" },
      { name: "TFWA World Exhibition (Travel Retail)", date: "Ekim (Cannes'dan Amsterdam'a taşındı)", participants: "35,000+" },
      { name: "Horecava (Gastronomi & Horeca)", date: "Ocak", participants: "68,000+" },
      { name: "Interclean Amsterdam (Temizlik)", date: "Mayıs (çift yıl)", participants: "40,000+" },
      { name: "Miljonairfair (Lüks)", date: "Kasım", participants: "25,000+" },
      { name: "Amsterdam Coffee Festival", date: "Nisan", participants: "30,000+" },
      { name: "PLMA World (Private Label)", date: "Mayıs", participants: "15,000+" },
      { name: "AMS IX NOG (Tech)", date: "Yıl boyu", participants: "Davetli" }
    ],
    services: [
      { title: "Dutch Minimal Tasarım",      description: "Fonksiyonelliği estetikle birleştiren Hollanda tasarım anlayışı", icon: "🌷" },
      { title: "Sürdürülebilir Standlar",    description: "Yeniden kullanılabilir, karbon nötr stand çözümleri",             icon: "🌱" },
      { title: "AV & Tech Entegrasyonu",     description: "ISE seviyesinde ses, görüntü ve otomasyon sistemleri",             icon: "🎵" },
      { title: "Modüler & Taşınabilir",      description: "Birden fazla fuarda kullanılabilen modüler sistemler",             icon: "🧩" },
      { title: "Grafik & Baskı",             description: "RAI Amsterdam için büyük format baskı ve dijital tabelalar",       icon: "🖨️" },
      { title: "Stand Lojistiği",            description: "Schiphol yakınlığı avantajıyla hızlı nakliye ve gümrük",           icon: "🚚" },
      { title: "Aydınlatma Tasarımı",        description: "Hollanda ışık mühendisliğiyle etkileyici fuar aydınlatması",      icon: "💡" },
      { title: "Premium Mobilya Kiralama",   description: "Ergonomik ve estetik Hollanda tasarımı fuar mobilyaları",         icon: "🪑" },
    ],
    whyChooseUs: [
      { title: "RAI Amsterdam Onaylı", description: "RAI'nin tüm teknik gereksinimlerine uyumlu ekipler", icon: Award },
      { title: "Schiphol Yakınlığı", description: "Havalimanına 15 dakika — global lojistik avantajı", icon: Clock },
      { title: "AB Çevre Standartları", description: "En yüksek sürdürülebilirlik sertifikalarına sahip firmalar", icon: Shield },
      { title: "Çok Dilli Hizmet", description: "Hollandaca, İngilizce, Almanca ve Türkçe destek", icon: Target }
    ],
    testimonials: [
      { name: "Jan de Vries", company: "AV Solutions NL", text: "ISE Amsterdam için stand yaptırırken ExpoStand üzerinden 3 farklı teklifle karşılaştırdık. Dutch efficiency ile harika bir süreç oldu.", rating: 5, event: "ISE Amsterdam" },
      { name: "Burcu Aydın", company: "Turkish Natural Foods", text: "Horecava'da Türk mutfağını tanıtmak için mükemmel bir stand yaptırdık. ExpoStand'ın hızı bizi çok etkiledi.", rating: 5, event: "Horecava Amsterdam" },
      { name: "Sophie Bakker", company: "Eco Packaging NL", text: "Sürdürülebilir ambalaj ürünlerimiz için Interclean'de çevre dostu malzeme kullanan bir contractor bulduk.", rating: 5, event: "Interclean Amsterdam" }
    ],
    faqs: [
      { question: "RAI Amsterdam'da stand kurulum saatleri nelerdir?", answer: "RAI standart olarak fuar öncesi 2-3 gün kurulum izni verir. Bazı büyük fuarlar için erken montaj mümkün olabilir. Contractor'larımız tüm zamanlama logistiğini yönetir." },
      { question: "Hollanda'da çalışma izni gerekiyor mu?", answer: "AB vatandaşı olmayan kurulum ekipleri için çalışma izni gerekebilir. Contractor'larımız bu prosedürleri önceden tamamlar." },
      { question: "Amsterdam'da stand taşımacılığı nasıl yapılır?", answer: "RAI'nin official freight handler'ları mevcuttur. Contractor'larımız alternatif ve genellikle daha uygun fiyatlı çözümler de sunabilir." },
      { question: "ISE gibi büyük fuarlar için ne kadar önceden hazırlanmalıyım?", answer: "ISE için en az 9-12 ay önceden başlamanızı öneririz. Popüler stand pozisyonları çok erken dolar ve iyi contractor'ların takvimi de hızla kapanır." }
    ]
  },
  barcelona: {
    name: "Barcelona",
    country: "İspanya",
    title: "Barcelona Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Barcelona'da profesyonel fuar standı tasarımı. Fira de Barcelona'da MWC, ISC West ve Alimentaria için stand yapımı. Hızlı teklif alın!",
    keywords: "Barcelona fuar standı, Barcelona exhibition stand, Fira Barcelona stand, MWC Barcelona, Mobile World Congress stand, Barcelona exhibition contractors",
    description: "Barcelona, Fira de Barcelona'nın 200.000 m²'lik alanıyla İspanya'nın fuar başkentidir. Mobile World Congress (MWC — dünyanın en büyük mobil teknoloji fuarı), Alimentaria (Gıda & İçecek), Gastech ve Smart City Expo gibi global etkinliklere ev sahipliği yapar. Gaudi mimarisinin ilham verdiği yaratıcı tasarım anlayışıyla Barcelona, standlarına özgün bir kimlik kazandırmak isteyen markalar için ideal.",
    detailedDescription: "ExpoStand üzerinden Katalan yaratıcılığını yansıtan deneyimli stand contractor'larına ulaşın. İspanya'nın en dinamik fuar sahnesinde öne çıkın.",
    images: [
      "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1464790719320-516ecd75af6c?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1560969184-10fe8719e047?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: { suppliers: 155, contractors: 80, annualEvents: 70, visitors: "11M+", experience: "16+", projects: "2400+" },
    venues: [
      { name: "Fira Barcelona Gran Via", area: "200,000 m²", halls: "8 Hall", location: "L'Hospitalet de Llobregat", description: "İspanya'nın en büyük fuar kompleksi" },
      { name: "Fira Barcelona Montjuïc", area: "160,000 m²", halls: "12 Hall", location: "Montjuïc Tepesi", description: "Tarihi 1929 Dünya Fuarı'nın mirası" },
      { name: "CCIB (Convention Centre)", area: "57,000 m²", halls: "44 Salon", location: "Diagonal Mar", description: "Deniz kenarında modern kongre merkezi" }
    ],
    majorEvents: [
      { name: "Mobile World Congress (MWC)", date: "Şubat/Mart", participants: "100,000+" },
      { name: "Alimentaria (Gıda & İçecek)", date: "Nisan (çift yıl)", participants: "140,000+" },
      { name: "Smart City Expo World", date: "Kasım", participants: "25,000+" },
      { name: "Gastech", date: "Eylül", participants: "50,000+" },
      { name: "ISC West (Güvenlik)", date: "Mart", participants: "30,000+" },
      { name: "Hostelco (Gastronomi)", date: "Ekim (çift yıl)", participants: "55,000+" },
      { name: "Barcelona Design Week", date: "Ekim/Kasım", participants: "250,000+" },
      { name: "EIBTM (Meetings & Events)", date: "Kasım", participants: "12,000+" }
    ],
    services: [
      { title: "Katalan Yaratıcı Tasarım",   description: "Gaudi'den ilham alan organik ve özgün stand tasarımları",        icon: "🏛️" },
      { title: "Tech Showcase Standları",    description: "MWC seviyesinde interaktif teknoloji teşhiri",                    icon: "📡" },
      { title: "Gıda & Gastronomi Teşhiri",  description: "Alimentaria ve Hostelco için özel gıda teşhir çözümleri",        icon: "🍷" },
      { title: "Outdoor & Açık Alan",        description: "Barselona iklimine uygun açık hava stand tasarımları",            icon: "☀️" },
      { title: "Büyük Format Baskı",         description: "Fira Barcelona Gran Via için devasa format baskı ve dijital",    icon: "🖨️" },
      { title: "Stand Lojistiği & Nakliye",  description: "Barcelona Liman ve havalimanı koordinasyonlu lojistik",           icon: "🚚" },
      { title: "Aydınlatma Sistemleri",      description: "Akdeniz güneşinin ilham verdiği etkileyici aydınlatma",           icon: "💡" },
      { title: "Modüler Stand Sistemleri",   description: "MWC'den Smart City'ye tüm fuarlar için modüler çözümler",        icon: "🧩" },
    ],
    whyChooseUs: [
      { title: "Fira Barcelona Akreditasyonu", description: "MWC gibi büyük fuarlar için özel akreditasyona sahip ekipler", icon: Award },
      { title: "Akdeniz Enerjisi", description: "Yaratıcı ve motive Barcelona contractor'ları", icon: Clock },
      { title: "İspanyolca + İngilizce", description: "Tüm süreçlerde çift dil desteği ve lokal bilgi", icon: Shield },
      { title: "Esnek Çözümler", description: "Startup'tan Fortune 500'e her ölçekte hizmet", icon: Target }
    ],
    testimonials: [
      { name: "Carlos Mendoza", company: "TechStartup Spain", text: "MWC Barcelona'da ilk fuarımız için ExpoStand'ı kullandık. Sınırlı bütçeyle harika bir stand ortaya çıktı. Barcelona contractor'ı çok yardımseverdi.", rating: 5, event: "Mobile World Congress" },
      { name: "Derya Özsoy", company: "Turkish Organic Foods", text: "Alimentaria Barcelona'da organik ürünlerimizi sergileyen muhteşem bir stand tasarlandı. ExpoStand ile karşılaştırmalı teklif almak çok kolaydı.", rating: 5, event: "Alimentaria Barcelona" },
      { name: "Montserrat Vila", company: "Smart Urban Solutions", text: "Smart City Expo için akıllı şehir teknolojimizi sergileyen interaktif bir stand istedik. ExpoStand'dan bulduğumuz contractor hayalimizi gerçeğe dönüştürdü.", rating: 5, event: "Smart City Expo" }
    ],
    faqs: [
      { question: "MWC Barcelona için stand ne zaman sipariş edilmeli?", answer: "MWC, dünyanın en rekabetçi fuarlarından biri olduğundan en az 9-12 ay önceden harekete geçin. Premium hall pozisyonları 18 ay önceden dolabilir." },
      { question: "Fira Barcelona'nın güvenlik gereksinimleri nelerdir?", answer: "Yangın sınıfı A malzeme zorunluluğu ve yükseklik kısıtlamaları mevcuttur. Contractor'larımız tüm teknik compliance konularını yönetir." },
      { question: "Barcelona'da Türkçe konuşan contractor bulabilir miyim?", answer: "Platform üzerinde Türkçe destek sunan veya Türk firmalarla deneyimi olan birçok Barcelona contractor'ı mevcuttur." },
      { question: "Fira Gran Via ile Montjuïc arasındaki fark nedir?", answer: "Gran Via daha büyük ve modern (MWC buraya taşındı). Montjuïc daha tarihi ve küçük fuarlar için uygundur. Contractor'larımız her iki alanda da deneyimlidir." }
    ]
  },
  hannover: {
    name: "Hannover",
    country: "Almanya",
    title: "Hannover Fuar Standı Tasarım ve Uygulama Hizmetleri | ExpoStand",
    metaDescription: "Hannover'da profesyonel fuar standı tasarımı. Hannover Messe'de CeBIT, EMO ve Agritechnica için stand yapımı. Hızlı teklif alın!",
    keywords: "Hannover fuar standı, Hannover Messe stand, Hannover exhibition stand, Agritechnica stand, EMO Hannover, CeBIT stand yapımı, Hannover exhibition contractors",
    description: "Hannover, 463.000 m²'lik alanıyla dünyanın en büyük fuar tesisine ev sahipliği yapar: Deutsche Messe Hannover. Hannover Messe (Endüstri 4.0), Agritechnica (Tarım Makineleri — dünyanın en büyüğü), EMO Hannover (Metal İşleme), IAA Transportation (Ticari Araçlar) ve dsCEBIT'in miras kentleri fuarı olan Hannover, global B2B ticaretinin kalbidir. Almanya'nın endüstri başkentinde öne çıkmak için ExpoStand.",
    detailedDescription: "Deutsche Messe'nin zorlu teknik standartlarına hakim contractor'larla anahtar teslim fuar standı yapımı. Hannover'ın endüstriyel ruhuna uygun güçlü ve etkileyici standlar için teklif alın.",
    images: [
      "https://images.unsplash.com/photo-1578662996442-48f60103fc96?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1505761671935-60b3a7427bad?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1566404791232-af9fe8ae8677?q=80&w=1600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop"
    ],
    stats: { suppliers: 220, contractors: 120, annualEvents: 40, visitors: "25M+", experience: "25+", projects: "5000+" },
    venues: [
      { name: "Deutsche Messe Hannover", area: "463,000 m²", halls: "27 Hall", location: "Messegelände", description: "Dünyanın en büyük fuar alanı" },
      { name: "Convention Center (HCC)", area: "15,000 m²", halls: "25 Salon", location: "Messegelände", description: "Uluslararası kongre ve B2B etkinlikler" },
      { name: "Hannover Congress Centrum", area: "20,000 m²", halls: "Çoklu", location: "Şehir Merkezi", description: "Şehir içinde kongre merkezi" }
    ],
    majorEvents: [
      { name: "Hannover Messe (Endüstri)", date: "Nisan", participants: "120,000+" },
      { name: "Agritechnica (Tarım)", date: "Kasım (tek yıl)", participants: "450,000+" },
      { name: "EMO Hannover (Metal)", date: "Eylül (çift yıl)", participants: "130,000+" },
      { name: "IAA Transportation (Ticari)", date: "Eylül (çift yıl)", participants: "100,000+" },
      { name: "LIGNA (Ahşap & Orman)", date: "Mayıs (tek yıl)", participants: "105,000+" },
      { name: "INTERSCHUTZ (İtfaiye)", date: "Haziran (beş yılda)", participants: "150,000+" },
      { name: "GreenTech Festival", date: "Haziran", participants: "20,000+" },
      { name: "E-world (Enerji)", date: "Şubat", participants: "27,000+" }
    ],
    services: [
      { title: "Endüstri 4.0 Stand Tasarımı", description: "Hannover Messe için makine teşhiri ve otomasyon showcase",     icon: "🤖" },
      { title: "Tarım Makineleri Teşhiri",    description: "Agritechnica için büyük ekipman kurulumu ve açık alan standları", icon: "🚜" },
      { title: "Ağır Sanayi Standları",       description: "EMO için metal işleme ekipmanı teşhiri özel platform yapıları", icon: "⚙️" },
      { title: "Outdoor Alanlar",             description: "Açık alan ekipman teşhiri için çatılı platform sistemleri",     icon: "🏗️" },
      { title: "Grafik & Sinyal Sistemleri",  description: "Deutsche Messe'nin 27 salonunda etkili grafik ve tabela",       icon: "🖨️" },
      { title: "Stand Lojistiği & Depolama",  description: "Ağır ekipman taşıma, forklift koordinasyonu ve depolama",      icon: "🚚" },
      { title: "Aydınlatma & Ses Sistemleri", description: "Endüstriyel fuarlar için güçlü ve sağlam AV çözümleri",        icon: "🔊" },
      { title: "Modüler Stand Çözümleri",     description: "Farklı Messe hallerinde tekrar kullanılabilir modüler standlar", icon: "🧩" },
    ],
    whyChooseUs: [
      { title: "Deutsche Messe Akreditasyonu", description: "27 halldeki tüm teknik kurallara hakim akredite ekipler", icon: Award },
      { title: "25+ Yıl Hannover Deneyimi", description: "Her fuarda kazanılmış lojistik ve kurulum uzmanlığı", icon: Clock },
      { title: "Ağır Ekipman Deneyimi", description: "Ton'larca ağır makine kurulumunda kanıtlanmış güvenlik", icon: Shield },
      { title: "Rekabetçi Alman Fiyatları", description: "Hannover bölgesinden yerel firmalarla optimum maliyet", icon: Target }
    ],
    testimonials: [
      { name: "Thomas Bauer", company: "Industrial Automation GmbH", text: "Hannover Messe'de robot kollarımızı sergileyen muhteşem bir stand yaptık. ExpoStand'dan bulduğumuz contractor teknik karmaşıklığı mükemmel yönetti.", rating: 5, event: "Hannover Messe" },
      { name: "Mehmet Ali Çelik", company: "Turkish Agricultural Machinery", text: "Agritechnica'da Türk tarım makinelerimizi tanıtmak için harika bir alan tasarlandı. ExpoStand ile Hannover'daki doğru contractor'ı bulduk.", rating: 5, event: "Agritechnica Hannover" },
      { name: "Peter Hoffmann", company: "Precision Tools DE", text: "EMO Hannover için hassas ölçüm aletlerimizin özel teşhir platformu istedik. Beklentilerimizin üzerinde bir sonuç çıktı.", rating: 5, event: "EMO Hannover" }
    ],
    faqs: [
      { question: "Hannover Messe için stand ne zaman sipariş edilmeli?", answer: "Hannover Messe için 9-12 ay önce harekete geçin. Türk katılımcılar için Hannover Messe'nin Türkiye temsilciliği (Türk Messe) ile erken koordinasyon önerilir." },
      { question: "Ağır ekipman kurulumu için özel gereksinimler var mı?", answer: "Zemin yük kapasitesi ve forklift erişimi için önceden venue koordinasyonu gerekir. Contractor'larımız bu teknik gereksinimleri önceden planlar." },
      { question: "Agritechnica açık alan standları nasıl çalışır?", answer: "Agritechnica'da büyük tarım makineleri için hem kapalı hem açık teşhir alanları mevcuttur. Açık alanlar için weather-proof platform yapıları kurulur." },
      { question: "Hannover'dan Frankfurt'a stand nakliyesi yapılabilir mi?", answer: "Evet, pek çok contractor her iki şehirde de aktiftir ve fuarlar arasında stand elemanlarının nakliyesini organize edebilir." }
    ]
  }
};


// ── Generic content builder for auto-created cities ──────────────────────────
// Uses AI-generated content (ai_content) when available, falls back to generic template
function buildGenericCityData(data) {
  const name = data.name;
  const country = data.country || '';
  const img = data.image || 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?q=80&w=1600&auto=format&fit=crop';
  const contractorCount = data.contractors_count || 0;
  const ai = data.ai_content || {};

  // ── Venues ───────────────────────────────────────────────────────────────
  const venues = (ai.venues && ai.venues.length > 0) ? ai.venues : [
    { name: `${name} Exhibition & Convention Center`, area: '—', location: name, description: `${name}'nin ana fuar ve kongre merkezi` },
    { name: `${name} International Trade Fair`, area: '—', location: country, description: 'Uluslararası ticaret fuarlarına ev sahipliği yapan prestijli mekan' },
  ];

  // ── Major Events ─────────────────────────────────────────────────────────
  const majorEvents = (ai.major_events && ai.major_events.length > 0)
    ? ai.major_events.map(e => ({ name: e.name, date: e.date, participants: e.participants }))
    : [];

  // ── FAQ ──────────────────────────────────────────────────────────────────
  const faqs = (ai.faq && ai.faq.length > 0) ? ai.faq : [
    { question: `${name}'de fuar standı yaptırmak ne kadar sürer?`,
      answer: `Proje büyüklüğüne bağlı olarak genellikle 2-6 hafta arasında tamamlanır. ${name}'deki brief oluşturduktan sonra 24 saat içinde teklifler gelmeye başlar.` },
    { question: `${name}'de nasıl teklif alabilirim?`,
      answer: `Platformda brief oluşturun, doğrulanmış contractor'lar size teklif göndersin. Teklifleri karşılaştırın ve en uygununu seçin.` },
    { question: `${name}'deki stand fiyatları ne kadar?`,
      answer: `Stand büyüklüğü, tasarım karmaşıklığı ve malzemelere göre değişir. Birden fazla teklif alarak en rekabetçi fiyatı bulabilirsiniz.` },
    { question: `${country ? country + ' için ' : ''}vize veya lojistik desteği sağlıyor musunuz?`,
      answer: `${name}'deki contractor'larımız yerel lojistik, gümrük ve nakliye süreçlerinde size yol gösterebilir.` },
    { question: `Sadece stand yapımı mı, yoksa tasarım da mı alınabilir?`,
      answer: `Her ikisi de mümkün! ${name}'deki contractor'larımız konsept tasarımdan anahtar teslime kadar tüm süreci yönetebilir.` },
  ];

  // ── Why Choose ───────────────────────────────────────────────────────────
  const whyChooseUs = (ai.why_choose && ai.why_choose.length > 0)
    ? ai.why_choose.map(w => ({ title: w.title, description: w.description, icon: w.icon }))
    : [
      { title: `${name}'e Özel Ağ`,          description: `${name} pazarında uzmanlaşmış doğrulanmış contractor ağı`,        icon: '🏆' },
      { title: 'Hızlı Teklif Süreci',         description: `24 saat içinde ${name}'den birden fazla teklif alın`,            icon: '⚡' },
      { title: 'Rekabetçi Fiyatlar',          description: 'Fiyatları karşılaştırın, en uygun teklifi seçin',               icon: '💰' },
      { title: 'Güvenli Ödeme',               description: 'Platform güvencesiyle, iş tamamlandığında ödeme yapın',         icon: '🔒' },
    ];

  return {
    name,
    country,
    flag: data.flag || '🌍',
    title: `${name} Exhibition Stand Builder | ExpoStand`,
    metaDescription: `Find professional exhibition stand builders in ${name}. Get instant quotes from verified contractors. Fast, reliable, professional.`,
    heroImage: img,
    images: [
      img,
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1561457213-a526e5beb0c8?q=80&w=1600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1524420533731-4c6c2338cefc?q=80&w=1600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?q=80&w=1600&auto=format&fit=crop',
    ],
    tagline: data.tagline || ai.tagline || `${name} — International Exhibition Destination`,
    description: data.description || ai.description || `${name}, ${country ? country + "'nin " : ''}uluslararası fuar ve sergi alanında önemli destinasyonlarından biridir.`,
    detailedDescription: `Tüm contractor'larımız deneyim ve referanslarına göre filtrelenmiştir. 24 saat içinde teklif alın, güvenli ödeme ve standart sözleşmelerle çalışın.`,
    contractors: contractorCount,
    stats: {
      suppliers: contractorCount > 0 ? Math.round(contractorCount * 1.8) : '—',
      contractors: contractorCount || '—',
      annualEvents: majorEvents.length > 0 ? majorEvents.length : '—',
      visitors: '—',
      experience: '10+',
      projects: contractorCount > 0 ? `${contractorCount * 15}+` : '—',
    },
    venues,
    majorEvents,
    services: [
      { title: 'Stand Tasarım Uzmanları',    description: "Yaratıcı tasarım yapan contractor'lardan teklif alın",            icon: '🎨' },
      { title: 'Stand Kurulum Firmaları',     description: "Deneyimli kurulum ekipleri ile çalışan contractor'lar",           icon: '🔨' },
      { title: 'AV Teknoloji Sağlayıcıları', description: 'LED, ses sistemi, interaktif çözümler sunan firmalar',             icon: '📺' },
      { title: 'Lojistik & Nakliye',          description: 'Stand malzemelerinin güvenli taşınması ve depolama',              icon: '🚚' },
      { title: 'Özel İnşaat Çözümleri',       description: 'Modüler ve özel stand inşaatı, anahtar teslim projeler',         icon: '🏗️' },
      { title: 'Aydınlatma Tasarımı',         description: 'Profesyonel fuar aydınlatma ve atmosfer yönetimi',               icon: '💡' },
      { title: 'Grafik & Baskı',              description: 'Büyük format baskı, branding görselleri ve tabelalar',           icon: '🖨️' },
      { title: 'Fuar Mobilyası',              description: 'Kiralık veya satın alınabilir stand mobilyaları ve ekipmanlar',  icon: '🪑' },
    ],
    whyChooseUs,
    faqs,
    testimonials: [],
    highlights: [],
    otherCities: [],
    cityInfo: { name, country, images: [img] },
    isAutoCreated: true,
  };
}



export default function CityDetail() {
  const { citySlug } = useParams();
  const staticCity = cityData[citySlug];
  
  const [city, setCity] = useState(staticCity || null);
  const [loadingCity, setLoadingCity] = useState(!staticCity);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [liveFairs, setLiveFairs] = useState([]);
  const [fairsLoading, setFairsLoading] = useState(false);
  const [fairCategory, setFairCategory] = useState('all');
  
  const [formData, setFormData] = useState({
    name: "",
    email: "", 
    company: "",
    event: "",
    boothSize: "",
    budget: "",
    message: "",
    consent: false
  });

  // Fetch dynamic city data for auto-created cities
  useEffect(() => {
    if (staticCity) return; // Already have static data
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    fetch(`${API}/public/city/${citySlug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setCity(buildGenericCityData(data));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCity(false));
  }, [citySlug, staticCity]);

  // Fetch live fairs from DB for this city
  useEffect(() => {
    if (!citySlug) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const searchName = citySlug
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    setFairsLoading(true);
    const catParam = fairCategory !== 'all' ? `&category=${encodeURIComponent(fairCategory)}` : '';
    fetch(`${API}/public/fairs?city=${encodeURIComponent(searchName)}&limit=12&with_contractor_count=true${catParam}`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => {
        // Öne çıkan fuarlar önce gelsin
        const sorted = (data.items || []).sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
        setLiveFairs(sorted);
      })
      .catch(() => {})
      .finally(() => setFairsLoading(false));
  }, [citySlug, fairCategory]);

  // Dropdown dışına tıklanınca kapat — MUST be before any early returns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.city-dropdown')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isDropdownOpen]);

  // SEO Meta etiketlerini ayarla - Using react-helmet-async
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalBase = 'https://expostand.com';

  if (loadingCity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Şehir yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Şehir bulunamadı</h1>
          <Link to="/locations" className="text-blue-600 hover:underline">Tüm şehirlere dön</Link>
        </div>
      </div>
    );
  }

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* React Helmet for SEO */}
      <Helmet>
        <title>{city?.title || `${city?.name} Exhibition Stand Builder | ExpoStand`}</title>
        <meta name="description" content={city?.metaDescription || city?.description || ''} />
        <meta name="keywords" content={city?.keywords || ''} />
        <link rel="canonical" href={`${canonicalBase}/city/${citySlug}`} />
        <link rel="alternate" hrefLang="tr" href={`${canonicalBase}/city/${citySlug}`} />
        <link rel="alternate" hrefLang="en" href={`${canonicalBase}/en/city/${citySlug}`} />
        <link rel="alternate" hrefLang="x-default" href={`${canonicalBase}/city/${citySlug}`} />
        {/* Open Graph */}
        <meta property="og:title" content={city?.title || `${city?.name} Exhibition Stand Builder | ExpoStand`} />
        <meta property="og:description" content={city?.metaDescription || city?.description || ''} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${canonicalBase}/city/${citySlug}`} />
        {city?.images?.[0] && <meta property="og:image" content={city.images[0]} />}
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": `ExpoStand ${city?.name}`,
          "description": city?.description || '',
          "address": { "@type": "PostalAddress", "addressLocality": city?.name, "addressCountry": city?.country },
          "url": `${canonicalBase}/city/${citySlug}`,
          "image": city?.images?.[0] || '',
          "priceRange": "$$",
          "serviceArea": city?.name,
        })}</script>
      </Helmet>

      {/* HEADER */}
      <header className="bg-white px-6 py-4 relative z-50 border-b border-slate-200">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
              alt="EXPO STAND Logo" 
              className="w-12 h-10" 
            />
            <div className="text-slate-700">
              <div className="text-xl font-semibold leading-tight">EXPO</div>
              <div className="text-xl font-semibold leading-tight">STAND</div>
            </div>
          </a>
          
          {/* Navigation */}
          <div className="flex items-center space-x-6 text-slate-700 text-sm">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>hello@expostand.com</span>
            </div>
            
            {/* Sign In Dropdown */}
            <div className="relative group">
              <span className="hover:text-blue-600 cursor-pointer flex items-center">
                Sign In <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Exhibitor
                </a>
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Contractor
                </a>
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Supplier
                </a>
              </div>
            </div>
            
            {/* Sign Up Dropdown */}
            <div className="relative group">
              <span className="hover:text-blue-600 cursor-pointer flex items-center">
                Sign Up <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/exhibitor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Exhibitor
                </a>
                <a href="/contractor/signup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Contractor
                </a>
                <a href="/supplier/signup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Supplier
                </a>
              </div>
            </div>
            
            <span className="hover:text-blue-600 cursor-pointer">Contact</span>
            <a 
              href="/admin-login"
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm rounded"
            >
              Admin Giriş
            </a>
          </div>
        </div>
      </header>

      {/* HERO SECTION WITH STATS */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-6 overflow-hidden" style={{ height: 'calc(100vh - 80px)', maxHeight: '688px' }}>
        {/* City Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
          style={{
            backgroundImage: `url('${city.images?.[0] || city.heroImage || ''}')`,
            backgroundPosition: 'center 20%'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/20 to-white/40"></div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 h-full flex flex-col">
          
          {/* City Selector - Top Right */}
          <div className="flex justify-end mb-4">
            <div className="relative city-dropdown">
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-slate-200/50 rounded-xl px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <Globe2 className="h-4 w-4" />
                <span>Diğer Şehirler</span>
                <svg className={`h-3 w-3 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 py-4 min-w-[320px] z-50">
                <div className="px-4 pb-3 mb-3 border-b border-slate-100">
                  <p className="text-sm text-slate-700 font-semibold mb-3">Diğer Şehirlerdeki Contractor'lar</p>
                  
                  {/* Arama Kutusu */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Şehir ara..."
                      className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onChange={(e) => {
                        const searchTerm = e.target.value.toLowerCase();
                        const cityItems = document.querySelectorAll('.city-dropdown-item');
                        cityItems.forEach(item => {
                          const cityName = item.dataset.cityName.toLowerCase();
                          const countryName = item.dataset.countryName.toLowerCase();
                          if (cityName.includes(searchTerm) || countryName.includes(searchTerm)) {
                            item.style.display = 'flex';
                          } else {
                            item.style.display = 'none';
                          }
                        });
                      }}
                    />
                    <svg className="absolute left-2.5 top-2.5 h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {Object.entries(cityData).map(([slug, cityInfo]) => {
                    if (slug === citySlug) return null; // Mevcut şehri gösterme
                    return (
                      <a
                        key={slug}
                        href={`/city/${slug}`}
                        className="city-dropdown-item flex items-center justify-between gap-4 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        data-city-name={cityInfo.name}
                        data-country-name={cityInfo.country}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-7 rounded overflow-hidden flex-shrink-0">
                            <img 
                              src={cityInfo.images[0]} 
                              alt={cityInfo.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{cityInfo.name}</div>
                            <div className="text-xs text-slate-500 truncate">{cityInfo.country}</div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end flex-shrink-0">
                          <div className="text-sm font-bold text-blue-600">
                            {cityInfo.stats.contractors}
                          </div>
                          <div className="text-xs text-slate-500">
                            contractor
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
                
                <div className="mt-3 pt-3 px-4 border-t border-slate-100 flex items-center justify-between">
                  <a 
                    href="/" 
                    className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Ana Sayfa
                  </a>
                  <a 
                    href="/locations" 
                    className="flex items-center gap-1.5 text-sm text-blue-600 font-semibold hover:underline"
                  >
                    Tüm Şehirler
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </a>
                </div>
                </div>
              )}
            </div>
          </div>

          {/* Hero Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center flex-1">
            {/* Sol Taraf - Content Block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm mb-4">
                <MapPin className="h-4 w-4 text-blue-600" />
                <span className="font-medium">{city.country}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
                <span className="text-blue-600">{city.name}</span><br />
                <span className="text-slate-800">Fuar Standı</span><br />
                <span className="text-slate-700">Contractor Ağı</span>
              </h1>
              
              <p className="text-base md:text-lg leading-6 text-slate-600 mb-4">
                {city.description}
              </p>

              <div className="space-y-2 mb-6">
                <p className="text-sm text-slate-700">
                  <strong>🎯 Doğrulanmış Contractor'lar</strong> - Tüm firmalar referans kontrolünden geçer
                </p>
                <p className="text-sm text-slate-700">
                  <strong>⚡ Hızlı Eşleştirme</strong> - 24 saat içinde size uygun firmalarla buluşun
                </p>
                <p className="text-sm text-slate-700">
                  <strong>🏆 Çoklu Teklif</strong> - Birden fazla contractor'dan teklif alıp karşılaştırın
                </p>
                <p className="text-sm text-slate-700">
                  <strong>🔐 Güvenli İşlem</strong> - Platform üzerinden güvenli ödeme imkanı
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <motion.a
                  href="#hizmet-al"
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-2xl font-semibold shadow-lg hover:bg-blue-700 transition-all duration-300"
                >
                  <Zap className="h-5 w-5" />
                  Contractor'larla Eşleş
                </motion.a>
                <motion.a
                  href="#projelerimiz"
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex items-center gap-2 border-2 border-slate-300 bg-white text-slate-900 px-5 py-3 rounded-2xl font-semibold shadow-sm hover:bg-slate-50 transition-all duration-300"
                >
                  <Star className="h-5 w-5" />
                  Projeleri İncele
                </motion.a>
              </div>

              <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Şeffaf teklifler</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Güvenli ödemeler</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Standart sözleşmeler</li>
              </ul>
            </motion.div>

            {/* Sağ Taraf - İstanbul Görsel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={city.images?.[0] || city.heroImage || 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?q=80&w=1600&auto=format&fit=crop'}
                  alt={`${city.name} fuar merkezleri ve contractor'ları`}
                  className="w-full h-[280px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                    <h3 className="text-sm font-bold mb-1">{city.name} Contractor Ağı</h3>
                    <div className="flex items-center gap-3 text-xs">
                      {city.venues && city.venues.slice(0, 3).map((v, i) => (
                        <span key={i}>✓ {v.name.split(' ').slice(0, 2).join(' ')}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Compact İstatistikler */}
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-md border border-slate-200/50"
            >
              <div className="text-xl font-bold text-blue-600 mb-1">{city.stats.suppliers}</div>
              <div className="text-xs text-slate-600 font-medium">Tedarikçi</div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-md border border-slate-200/50"
            >
              <div className="text-xl font-bold text-green-600 mb-1">{city.stats.contractors}</div>
              <div className="text-xs text-slate-600 font-medium">Contractor</div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-md border border-slate-200/50"
            >
              <div className="text-xl font-bold text-purple-600 mb-1">{city.stats.annualEvents}</div>
              <div className="text-xs text-slate-600 font-medium">Yıllık Fuar</div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-md border border-slate-200/50"
            >
              <div className="text-xl font-bold text-orange-600 mb-1">{city.stats.visitors}</div>
              <div className="text-xs text-slate-600 font-medium">Ziyaretçi</div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-md border border-slate-200/50"
            >
              <div className="text-xl font-bold text-red-600 mb-1">{city.stats.experience}</div>
              <div className="text-xs text-slate-600 font-medium">Yıl Deneyim</div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-md border border-slate-200/50"
            >
              <div className="text-xl font-bold text-indigo-600 mb-1">{city.stats.projects}</div>
              <div className="text-xs text-slate-600 font-medium">Proje</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FUAR MERKEZLERİ */}
      <section className="py-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {city.name} Fuar Merkezleri
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {city.country}'deki en büyük fuar merkezlerinde deneyimli contractor ağımızla hizmet veriyoruz
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {city.venues && city.venues.map((venue, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{venue.name}</h3>
                  <p className="text-slate-600 mb-4">{venue.description}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Alan:</span>
                      <span className="font-semibold text-slate-900">{venue.area}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Salon:</span>
                      <span className="font-semibold text-slate-900">{venue.halls}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Konum:</span>
                      <span className="font-semibold text-slate-900">{venue.location}</span>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <span className="inline-flex items-center gap-2 text-blue-600 font-medium">
                    <Building2 className="h-4 w-4" />
                    Aktif Contractor Ağı
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* HİZMETLERİMİZ */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">
              Contractor'larımızın Hizmet Alanları
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {city.services && city.services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h4 className="text-lg font-bold text-slate-900 mb-3">{service.title}</h4>
                  <p className="text-slate-600 text-sm">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* NEDEN BİZ */}
          <div>
            <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">
              Neden ExpoStand {city.name}?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {city.whyChooseUs && city.whyChooseUs.map((reason, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="text-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200"
                >
                  <reason.icon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-slate-900 mb-3">{reason.title}</h4>
                  <p className="text-slate-600 text-sm">{reason.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BAŞLICA ETKİNLİKLER */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {city.name}'daki Büyük Fuarlar
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              {city.country ? `${city.country}'deki` : 'Dünyanın'} en prestijli fuarlarda profesyonel stand çözümlerimizle yanınızdayız
            </p>
            {/* Category Filter Chips */}
            <div className="flex flex-wrap justify-center gap-2">
              {['all', 'Teknoloji', 'Sanayi & Makine', 'Gıda & İçecek', 'Moda & Tekstil', 'Otomotiv', 'İnşaat & Gayrimenkul', 'Sağlık & Medikal', 'Mobilya & Tasarım', 'Enerji', 'Diğer'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setFairCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    fairCategory === cat
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600'
                  }`}
                >
                  {cat === 'all' ? 'Tümü' : cat}
                </button>
              ))}
            </div>
          </div>
          
          {fairsLoading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : liveFairs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {liveFairs.map((fair, index) => (
              <motion.div
                key={fair.fair_id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <Link
                  to={`/fair/${fair.fair_id}`}
                  className={`block rounded-2xl p-6 border transition-all duration-300 flex flex-col h-full group relative overflow-hidden
                    ${fair.is_featured
                      ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300 hover:shadow-xl hover:border-amber-400 ring-1 ring-amber-200'
                      : 'bg-gradient-to-br from-slate-50 to-blue-50 border-slate-200 hover:shadow-lg hover:border-blue-300'
                    }`}
                >
                  {fair.is_featured && (
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-400 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow">
                      <span>★</span> ÖNE ÇIKAN
                    </div>
                  )}
                  <Calendar className={`h-7 w-7 mb-3 group-hover:scale-110 transition-transform ${fair.is_featured ? 'text-amber-500' : 'text-blue-600'}`} />
                  <h3 className={`font-bold mb-1 text-sm leading-tight flex-1 ${fair.is_featured ? 'text-amber-900 group-hover:text-amber-700' : 'text-slate-900 group-hover:text-blue-700'}`}>{fair.name}</h3>
                  {fair.venue && <p className="text-xs text-slate-500 mb-2 truncate">{fair.venue}</p>}
                  <div className="mt-auto space-y-1">
                    {fair.start_date && (
                      <div className={`font-semibold text-xs ${fair.is_featured ? 'text-amber-600' : 'text-blue-600'}`}>
                        {fair.start_date.slice(0, 7).replace('-', '/')}
                        {fair.end_date && fair.end_date !== fair.start_date ? ` – ${fair.end_date.slice(5, 7)}/${fair.end_date.slice(0, 4)}` : ''}
                      </div>
                    )}
                    {fair.visitors && fair.visitors !== '—' && (
                      <div className="text-slate-500 text-xs">{fair.visitors} ziyaretçi</div>
                    )}
                    {fair.frequency && <div className="text-slate-400 text-xs">{fair.frequency}</div>}
                    {fair.contractor_count > 0 && (
                      <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full w-fit mt-1">
                        <span>✓</span>
                        <span>{fair.contractor_count} contractor</span>
                      </div>
                    )}
                    <div className={`text-xs font-medium group-hover:underline mt-1 ${fair.is_featured ? 'text-amber-600' : 'text-blue-500'}`}>Detayları Gör →</div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          ) : (
            /* Eğer canlı fuar yoksa static city verisi ya da placeholder */
            (!city.majorEvents || city.majorEvents.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-16 px-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border border-blue-100 mb-16">
              <div className="text-5xl mb-5">🗓️</div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Yakında Burada!</h3>
              <p className="text-slate-500 text-center max-w-lg">
                {city.name}'daki büyük fuar etkinlikleri en kısa sürede listelenecektir.
                Ekibimiz hâlihazırda {city.name} pazarını araştırıyor — beklemede kalın! 🚀
              </p>
            </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {city.majorEvents.map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="text-center">
                    <Calendar className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                    <h3 className="font-bold text-slate-900 mb-2 text-sm">{event.name}</h3>
                    <div className="text-blue-600 font-semibold text-sm mb-1">{event.date}</div>
                    <div className="text-slate-600 text-xs">{event.participants} katılımcı</div>
                  </div>
                </motion.div>
              ))}
            </div>
            )
          )}

          {/* MÜŞTERİ DEĞERLENDİRMELERİ */}
          <div>
            <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">
              Müşteri Deneyimleri
            </h3>

            {/* Gerçek veri varsa göster, yoksa espirili placeholder */}
            {city.testimonials && city.testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {city.testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <div className="font-bold text-slate-900">{testimonial.name}</div>
                    <div className="text-blue-600 font-medium">{testimonial.company}</div>
                    <div className="text-slate-500 text-sm">{testimonial.event}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            ) : (
            <div className="flex flex-col items-center justify-center py-14 px-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border border-amber-100">
              <div className="text-5xl mb-5">⭐</div>
              <h4 className="text-xl font-bold text-slate-800 mb-2">Yorumlar Yolda!</h4>
              <p className="text-slate-500 text-center max-w-lg text-base leading-relaxed">
                {city.name}'deki ilk müşterilerimiz projeyi teslim aldığında bu bölüm gerçek deneyimlerle dolacak.
                Uydurma yorum koymayı sevmeyiz — burada sadece gerçekler konuşur. 🤝
              </p>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* GALERİ */}
      <section id="projelerimiz" className="py-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {city.name}'daki Projelerimiz
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Farklı sektörlerden başarılı fuar standı projelerimizi inceleyin
            </p>
          </div>

          {/* Auto-created şehirlerde henüz proje yok mesajı */}
          {city.isAutoCreated ? (
            <div className="flex flex-col items-center justify-center py-20 px-8 bg-white rounded-3xl border-2 border-dashed border-slate-200">
              <div className="text-6xl mb-6">🔨</div>
              <h3 className="text-2xl font-bold text-slate-800 mb-3">Ustalar Harıl Harıl Çalışıyor!</h3>
              <p className="text-slate-500 text-center max-w-xl text-lg leading-relaxed">
                {city.name}'daki contractor'larımız henüz ilk siparişlerini bekliyor. 
                Bu bölümü doldurmak için can atıyorlar — siz brief oluşturun, onlar koşarak gelsin! 🏃‍♂️
              </p>
              <a href="/exhibitor/signup"
                className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-xl transition-colors shadow-lg hover:shadow-xl">
                İlk Projeyi Sen Başlat →
              </a>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(city.images || []).map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="relative overflow-hidden rounded-2xl shadow-lg group cursor-pointer"
              >
                <img
                  src={image}
                  alt={`${city.name} fuar standı projesi ${index + 1}`}
                  className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-lg font-semibold">Proje #{String(index + 1).padStart(2, '0')}</div>
                  <div className="text-sm opacity-90">{city.name} Fuar Standı</div>
                </div>
                <div className="absolute top-4 right-4">
                  <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Tamamlandı
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* HİZMET BUTONLARI */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {city.name}'de Fuar Hizmetleri
          </h2>
          <p className="text-lg text-slate-600 mb-12">
            Deneyimli contractor ve supplier ağımızdan anında teklif alın
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/get-quote"
              className="group relative overflow-hidden rounded-2xl bg-white border-2 border-blue-600 p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              <div className="relative z-10">
                <Building2 className="h-12 w-12 text-blue-600 group-hover:text-white mx-auto mb-4 transition-colors duration-300" />
                <h3 className="text-xl font-bold text-slate-900 group-hover:text-white mb-2 transition-colors duration-300">
                  Get a Brief
                </h3>
                <p className="text-slate-600 group-hover:text-blue-100 transition-colors duration-300">
                  Stand kurulum işleriniz için contractor teklifi alın
                </p>
                <ArrowRight className="h-5 w-5 text-blue-600 group-hover:text-white mx-auto mt-4 transition-colors duration-300" />
              </div>
            </Link>

            <a
              href="#suppliers-form"
              className="group relative overflow-hidden rounded-2xl bg-white border-2 border-green-600 p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-green-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              <div className="relative z-10">
                <Users className="h-12 w-12 text-green-600 group-hover:text-white mx-auto mb-4 transition-colors duration-300" />
                <h3 className="text-xl font-bold text-slate-900 group-hover:text-white mb-2 transition-colors duration-300">
                  Find a Supplier
                </h3>
                <p className="text-slate-600 group-hover:text-green-100 transition-colors duration-300">
                  Fuar malzemeleri ve hizmetleri için supplier bulun
                </p>
                <ArrowRight className="h-5 w-5 text-green-600 group-hover:text-white mx-auto mt-4 transition-colors duration-300" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* SSS - SEO için önemli */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Sık Sorulan Sorular
            </h2>
            <p className="text-xl text-slate-600">
              {city.name} fuar standı hizmetleri hakkında merak ettikleriniz
            </p>
          </div>
          
          <div className="space-y-6">
            {city.faqs && city.faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-6 border border-slate-200"
              >
                <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HİZMET AL FORMU */}
      <section id="hizmet-al" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {city.name} Contractor'larından Teklif Al
            </h2>
            <p className="text-xl text-slate-600">
              Deneyimli contractor'larımızdan ücretsiz teklif alın, hızlıca size uygun firmalarla eşleşin
            </p>
            <div className="flex items-center justify-center gap-6 mt-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-green-600" />
                <span>24 Saat İçinde Eşleştirme</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-600" />
                <span>Doğrulanmış Firmalar</span>
              </div>
              <div className="flex items-center gap-2">
                <ThumbsUp className="h-4 w-4 text-purple-600" />
                <span>Çoklu Teklif</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 p-8 md:p-12">
            <form className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => updateForm('name', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4 text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white transition-all duration-300"
                    placeholder="Adınız ve soyadınız"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    E-posta Adresi *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4 text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white transition-all duration-300"
                    placeholder="ornek@sirket.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Şirket/Kurum Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => updateForm('company', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4 text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white transition-all duration-300"
                    placeholder="Şirketinizin adı"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Katılacağınız Fuar *
                  </label>
                  <select
                    value={formData.event}
                    onChange={(e) => updateForm('event', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4 text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:bg-white transition-all duration-300"
                  >
                    <option value="">Fuar seçin</option>
                    <option value="Istanbul Jewelry Show">Istanbul Jewelry Show</option>
                    <option value="Turkeybuild Istanbul">Turkeybuild Istanbul</option>
                    <option value="CNR Food Istanbul">CNR Food Istanbul</option>
                    <option value="Istanbul Furniture Fair">Istanbul Furniture Fair</option>
                    <option value="Energy Istanbul">Energy Istanbul</option>
                    <option value="Istanbul Auto Show">Istanbul Auto Show</option>
                    <option value="other">Diğer fuar</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Stand Boyutu *
                  </label>
                  <select
                    value={formData.boothSize}
                    onChange={(e) => updateForm('boothSize', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4 text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:bg-white transition-all duration-300"
                  >
                    <option value="">Stand boyutu seçin</option>
                    <option value="9-18">9-18 m² (Standart)</option>
                    <option value="18-36">18-36 m² (Orta)</option>
                    <option value="36-72">36-72 m² (Büyük)</option>
                    <option value="72-144">72-144 m² (Çok Büyük)</option>
                    <option value="144+">144+ m² (Mega Stand)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Tahmini Bütçe *
                  </label>
                  <select
                    value={formData.budget}
                    onChange={(e) => updateForm('budget', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4 text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:bg-white transition-all duration-300"
                  >
                    <option value="">Bütçe aralığı</option>
                    <option value="25000-50000">₺25,000 - ₺50,000</option>
                    <option value="50000-100000">₺50,000 - ₺100,000</option>
                    <option value="100000-250000">₺100,000 - ₺250,000</option>
                    <option value="250000-500000">₺250,000 - ₺500,000</option>
                    <option value="500000+">₺500,000+</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Proje Detayları ve Özel İstekler
                </label>
                <textarea
                  rows={5}
                  value={formData.message}
                  onChange={(e) => updateForm('message', e.target.value)}
                  className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4 text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white transition-all duration-300"
                  placeholder="• Stand tasarım tercihleri
• AV teknoloji ihtiyaçları  
• Mobilya ve dekorasyon
• Catering hizmetleri
• Özel istekleriniz..."
                />
              </div>
              
              <div className="bg-slate-50 rounded-2xl p-6">
                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) => updateForm('consent', e.target.checked)}
                    className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 mt-0.5"
                  />
                  <span className="leading-relaxed">
                    {city.name}'daki contractor'lardan <strong>ücretsiz teklif almak</strong> istiyorum. 
                    Kişisel verilerimin işlenmesini ve uygun firmalarla eşleştirilmek üzere iletişim kurulmasını onaylıyorum. 
                    <span className="text-blue-600 font-medium">24 saat içinde</span> size uygun contractor'larla buluştururuz.
                  </span>
                </label>
              </div>
              
              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-700 hover:via-blue-800 hover:to-indigo-800 text-white font-bold py-5 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 inline-flex items-center justify-center gap-3 text-lg"
                >
                  <Zap className="h-6 w-6" />
                  Contractor'larla Eşleş - 24 Saat İçinde!
                </button>
                <p className="text-center text-xs text-slate-500 mt-3">
                  * Eşleştirme hizmeti tamamen ücretsizdir, contractor'lardan direkt teklif alırsınız
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center gap-2 text-xl font-bold mb-4 md:mb-0">
              <Globe2 className="h-6 w-6 text-blue-600" />
              <span>ExpoStand - {city.name}</span>
            </div>
            <p className="text-sm text-slate-500 text-center md:text-left">
              © 2025 ExpoStand. {city.name}'de güvenilir fuar hizmetleri.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}